(function () {
    "use strict";

    console.clear()
    // Cache DOM elements and avoid repeated queries
    const elements = {
        uploadBtn: null,
        modal: null,
        closeBtn: null,
        browseLink: null,
        fileInput: null,
        previewBlock: null,
        previewImg: null,
        removeBtn: null,
        loadingBlock: null,
        generateBtn: null,
        loadingBar: null,
        loadingLabel: null,
        dropZone: null,
        modalBody: null,
        generatingStep: null,
        generatingBar: null,
        generatingLabel: null,
        designSelection: null,
        designList: null,
        addToCartBtn: null,
        stepDisplay: null,
    };

    // State management
    const state = {
        isUploadSuccess: false,
        taskId: null,
        selectedVersion: null,
        backgroundImage: null,
        intervals: new Set(),
        eventSources: new Set(),
        isInitialized: false, // Flag to prevent double initialization
        cropper: null, // Cropper instance
        activeTasks: new Set(), // Track multiple active tasks
    };

    // Constants
    const CONSTANTS = {
        API_BASE_URL: "https://pah.up.railway.app/api",
        GEMINI_GENERATE_ENDPOINT: "/image-render/generate-with-name",
        VERSION_ENDPOINT: "/generate/version",
        STATUS_STREAM_ENDPOINT: "/orders/status-stream",
        BACKGROUND_IMAGE_URL:
            "https://cdn.shopify.com/s/files/1/0689/6274/6560/files/pah_mockup_room_8x10_554d8923-366a-49c8-9366-1a787d1a3431.webp?v=1742619234",
        WATERMARK_TEXT: "Princessandhero",
         WATERMARK_CONFIG: {
            opacity: 0.09,
            color: "#000000",
            fontSize: 26, // canvas.width / fontSize
            rotation: -Math.PI / 6,
            spacing: {
                x: 4.5,
                y: 4.0,
            },
        },
        STORAGE_KEYS: {
            UPLOADED_FILE_NAME: "uploadedFileName",
            TASK_ID: "taskId",
            SELECTED_VERSION: "selectedVersion_",
        },
        GENERATION_STEPS: [
            "Sketch basic head shape...",
            "Map facial feature placement...",
            "Block in skin tones...",
            "Establish light and shadow...",
            "Refine eyes and mouth...",
            "Soften edges and transitions...",
            "Add texture and details...",
            "Final touches and accents..."
        ]
    };

    // Utility functions
    const utils = {
        // Debounce function to limit function calls
        debounce(func, wait) {
            let timeout;
            return function executedFunction(...args) {
                const later = () => {
                    clearTimeout(timeout);
                    func(...args);
                };
                clearTimeout(timeout);
                timeout = setTimeout(later, wait);
            };
        },

        // Safe localStorage operations
        storage: {
            get(key) {
                try {
                    return localStorage.getItem(key);
                } catch (e) {
                    console.warn("localStorage get failed:", e);
                    return null;
                }
            },
            set(key, value) {
                try {
                    localStorage.setItem(key, value);
                } catch (e) {
                    console.warn("localStorage set failed:", e);
                }
            },
            remove(key) {
                try {
                    localStorage.removeItem(key);
                } catch (e) {
                    console.warn("localStorage remove failed:", e);
                }
            },
        },

        // Create and show toast with better accessibility
        showToast(message, duration = 2200) {
            let toast = document.getElementById("custom-toast");
            if (!toast) {
                toast = document.createElement("div");
                toast.id = "custom-toast";
                toast.setAttribute("role", "alert");
                toast.setAttribute("aria-live", "polite");
                Object.assign(toast.style, {
                    position: "fixed",
                    top: "32px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "rgba(44,44,44,0.95)",
                    color: "#fff",
                    padding: "14px 28px",
                    borderRadius: "8px",
                    fontSize: "17px",
                    zIndex: "99999",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
                    display: "none",
                    maxWidth: "90vw",
                    wordWrap: "break-word",
                });
                document.body.appendChild(toast);
            }
            toast.textContent = message;
            toast.style.display = "block";

            // Auto-hide with fade effect
            setTimeout(() => {
                toast.style.opacity = "0";
                toast.style.transition = "opacity 0.3s ease";
                setTimeout(() => {
                    toast.style.display = "none";
                    toast.style.opacity = "1";
                }, 300);
            }, duration);
        },

        // Fetch image as base64 with error handling
        async fetchImageAsBase64(url) {
            try {
                const response = await fetch(
                    /^https?:/.test(url) ? url : "https://" + url
                );
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const blob = await response.blob();
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
            } catch (error) {
                console.error("Failed to fetch image:", error);
                return null;
            }
        },

        // Preload images for faster rendering
        preloadImages(urls) {
            return Promise.all(
                urls.map(url => {
                    return new Promise((resolve) => {
                        const img = new Image();
                        img.crossOrigin = "anonymous";
                        img.onload = () => resolve(img);
                        img.onerror = () => resolve(null);
                        img.src = url;
                    });
                })
            );
        },

        // Optimized watermark processing with caching
        addWatermarkToBase64Optimized(base64OrUrl, callback, customConfig = {}) {
            if (!base64OrUrl) return callback(base64OrUrl);

            // Check if it's a URL or base64
            const isUrl = base64OrUrl.startsWith('http');

            // Check cache first (for base64)
            if (!isUrl) {
                const cacheKey = base64OrUrl.substring(0, 100) + JSON.stringify(customConfig);
                if (this.watermarkCache && this.watermarkCache[cacheKey]) {
                    return callback(this.watermarkCache[cacheKey]);
                }
            }

            const img = new Image();
            img.crossOrigin = "anonymous";

            img.onload = function () {
                // Use smaller canvas for faster processing
                const maxSize = 800;
                let canvasWidth = img.width;
                let canvasHeight = img.height;

                if (img.width > maxSize || img.height > maxSize) {
                    const ratio = Math.min(maxSize / img.width, maxSize / img.height);
                    canvasWidth = Math.round(img.width * ratio);
                    canvasHeight = Math.round(img.height * ratio);
                }

                const canvas = document.createElement("canvas");
                canvas.width = canvasWidth;
                canvas.height = canvasHeight;
                const ctx = canvas.getContext("2d");

                // Use high-quality scaling
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);

                // Merge custom config with default config
                const config = {
                    ...CONSTANTS.WATERMARK_CONFIG,
                    ...customConfig,
                };

                // Draw watermark
                const fontSize = Math.max(
                    14,
                    Math.round(canvas.width / config.fontSize)
                );
                ctx.save();

                // Tăng độ mờ để watermark đậm hơn
                ctx.globalAlpha = config.opacity;
                ctx.translate(canvas.width / 2, canvas.height / 2);
                ctx.rotate(config.rotation);
                ctx.font = `bold ${fontSize}px Arial`;

                // Sử dụng màu từ config
                ctx.fillStyle = config.color;
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";

                const textWidth = ctx.measureText(CONSTANTS.WATERMARK_TEXT).width;
                const stepX = textWidth + fontSize * config.spacing.x;
                const stepY = fontSize * config.spacing.y;

                // Vẽ watermark pattern
                for (let y = -canvas.height; y < canvas.height * 2; y += stepY) {
                    for (let x = -canvas.width; x < canvas.width * 2; x += stepX) {
                        ctx.fillText(CONSTANTS.WATERMARK_TEXT, x, y);
                    }
                }

                ctx.restore();
                ctx.globalAlpha = 1;

                const result = canvas.toDataURL('image/jpeg', 0.9); // Use JPEG with 90% quality for faster processing

                // Cache the result (only for base64)
                if (!isUrl && this.watermarkCache) {
                    const cacheKey = base64OrUrl.substring(0, 100) + JSON.stringify(customConfig);
                    this.watermarkCache[cacheKey] = result;
                }

                callback(result);
            }.bind(this);

            img.onerror = () => {
                // If watermarking fails, return original
                if (isUrl) {
                    callback(base64OrUrl); // Return URL as fallback
                } else {
                    callback(base64OrUrl); // Return base64 as fallback
                }
            };

            img.src = base64OrUrl;
        },

        // Initialize watermark cache
        initWatermarkCache() {
            if (!this.watermarkCache) {
                this.watermarkCache = {};
            }
        },

        // Clear watermark cache
        clearWatermarkCache() {
            if (this.watermarkCache) {
                this.watermarkCache = {};
            }
        },

        // Original watermark function (kept for compatibility)
        addWatermarkToBase64(base64, callback, customConfig = {}) {
            if (!base64) return callback(base64);

            const img = new Image();
            img.crossOrigin = "anonymous";

            img.onload = function () {
                const canvas = document.createElement("canvas");
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext("2d");

                ctx.drawImage(img, 0, 0);

                // Merge custom config with default config
                const config = {
                    ...CONSTANTS.WATERMARK_CONFIG,
                    ...customConfig,
                };

                // Draw watermark
                const fontSize = Math.max(
                    14,
                    Math.round(canvas.width / config.fontSize)
                );
                ctx.save();

                // Tăng độ mờ để watermark đậm hơn
                ctx.globalAlpha = config.opacity;
                ctx.translate(canvas.width / 2, canvas.height / 2);
                ctx.rotate(config.rotation);
                ctx.font = `bold ${fontSize}px Arial`;

                // Sử dụng màu từ config
                ctx.fillStyle = config.color;
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";

                const textWidth = ctx.measureText(CONSTANTS.WATERMARK_TEXT).width;
                const stepX = textWidth + fontSize * config.spacing.x;
                const stepY = fontSize * config.spacing.y;

                // Vẽ watermark pattern
                for (let y = -canvas.height; y < canvas.height * 2; y += stepY) {
                    for (let x = -canvas.width; x < canvas.width * 2; x += stepX) {
                        ctx.fillText(CONSTANTS.WATERMARK_TEXT, x, y);
                    }
                }

                ctx.restore();
                ctx.globalAlpha = 1;
                callback(canvas.toDataURL());
            };

            img.onerror = () => callback(base64);
            img.src = base64;
        },

        // Hàm tiện ích để thay đổi cấu hình watermark

        updateWatermarkConfig(newConfig) {
            Object.assign(CONSTANTS.WATERMARK_CONFIG, newConfig);
        },

        // Hàm tiện ích để lấy cấu hình watermark hiện tại
        getWatermarkConfig() {
            return { ...CONSTANTS.WATERMARK_CONFIG };
        },

        // Hàm demo để thay đổi màu watermark
        setWatermarkColor(color) {
            this.updateWatermarkConfig({ color });
        },

        // Hàm demo để thay đổi độ mờ watermark
        setWatermarkOpacity(opacity) {
            this.updateWatermarkConfig({ opacity });
        },

        // Hàm demo để thay đổi kích thước font watermark
        setWatermarkFontSize(fontSize) {
            this.updateWatermarkConfig({ fontSize });
        },

        getCartToken() {
            // Try to get cart token from various sources
            try {
                // From localStorage
                const cartToken = localStorage.getItem("cart_token");
                if (cartToken) return cartToken;

                // From cookies
                const cookies = document.cookie.split(";");
                for (let cookie of cookies) {
                    const [name, value] = cookie.trim().split("=");
                    if (name === "cart_token" || name === "cart") {
                        return value;
                    }
                }

                // From meta tag
                const cartMeta = document.querySelector('meta[name="cart-token"]');
                if (cartMeta) return cartMeta.getAttribute("content");

                // Generate a unique token
                return (
                    "cart_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9)
                );
            } catch (err) {
                console.warn("Error getting cart token:", err);
                return "cart_" + Date.now();
            }
        },

        getSessionId() {
            // Try to get session ID from various sources
            try {
                // From localStorage
                const sessionId = localStorage.getItem("session_id");
                if (sessionId) return sessionId;

                // From cookies
                const cookies = document.cookie.split(";");
                for (let cookie of cookies) {
                    const [name, value] = cookie.trim().split("=");
                    if (name === "session_id" || name === "_shopify_s") {
                        return value;
                    }
                }

                // Generate a unique session ID
                const newSessionId =
                    "session_" +
                    Date.now() +
                    "_" +
                    Math.random().toString(36).substr(2, 9);
                localStorage.setItem("session_id", newSessionId);
                return newSessionId;
            } catch (err) {
                console.warn("Error getting session ID:", err);
                return "session_" + Date.now();
            }
        },

        // Multi-task management
        addActiveTask(taskId) {
            state.activeTasks.add(taskId);
            this.storage.set('activeTasks', JSON.stringify(Array.from(state.activeTasks)));
            console.log('✅ Added task to active list:', taskId);
        },

        removeActiveTask(taskId) {
            state.activeTasks.delete(taskId);
            this.storage.set('activeTasks', JSON.stringify(Array.from(state.activeTasks)));
            console.log('❌ Removed task from active list:', taskId);
        },

        getActiveTasks() {
            try {
                const stored = this.storage.get('activeTasks');
                if (stored) {
                    const tasks = JSON.parse(stored);
                    state.activeTasks = new Set(tasks);
                    return Array.from(state.activeTasks);
                }
            } catch (e) {
                console.warn('Error loading active tasks:', e);
            }
            return Array.from(state.activeTasks);
        },

        getTaskImages(taskId) {
            const img1 = this.storage.get("generatedImage_" + taskId + "_1");
            const img2 = this.storage.get("generatedImage_" + taskId + "_2");
            return { img1, img2 };
        },

        getTaskVersion(taskId) {
            return this.storage.get(CONSTANTS.STORAGE_KEYS.SELECTED_VERSION + taskId) || "1";
        },

        cleanupTask(taskId) {
            // Remove task from active list
            this.removeActiveTask(taskId);

            // Clean up task-specific data
            this.storage.remove("generatedImage_" + taskId + "_1");
            this.storage.remove("generatedImage_" + taskId + "_2");
            this.storage.remove(CONSTANTS.STORAGE_KEYS.SELECTED_VERSION + taskId);

            console.log('🧹 Cleaned up task:', taskId);
        },

        // Debug function to show active tasks
        showActiveTasks() {
            const activeTasks = this.getActiveTasks();
            console.log('📋 Active Tasks:', activeTasks);
            activeTasks.forEach(taskId => {
                const { img1, img2 } = this.getTaskImages(taskId);
                const version = this.getTaskVersion(taskId);
                console.log(`  Task ${taskId}:`, { img1: !!img1, img2: !!img2, version });
            });
            return activeTasks;
        },
    };

    // Normalize option text to compare (strip emojis/symbols, unify spaces/case)
    function normalizeOptionText(text) {
        try {
            return String(text || "")
                .normalize("NFKD")
                .replace(/[\u0300-\u036f]/g, "") // remove diacritics
                .replace(/[^\w\s]/g, " ") // remove emojis/symbols/punctuation
                .replace(/\s+/g, " ")
                .trim()
                .toUpperCase();
        } catch (_) {
            return (text || "").toString().toUpperCase();
        }
    }

    // Normalize and derive SKU from product type + size selection
    function normalizeSku(sku) {
        return String(sku || "").trim().toUpperCase();
    }

    function normalizeSizeKey(sizeText) {
        const s = normalizeOptionText(sizeText).replace(/\s+/g, ""); // e.g. 12x18 or 08x12
        // Keep leading zeros to match keys used elsewhere (e.g., 08x12)
        return s;
    }

    function deriveSkuFromSelection(productTypeText, sizeText) {
        const typeN = normalizeOptionText(productTypeText);
        const sizeKey = normalizeSizeKey(sizeText);

        if (typeN.includes("DIGITAL")) {
            return "DIGITAL";
        }
        if (typeN.includes("FRAMED") && typeN.includes("CANVAS")) {
            if (sizeKey === "08X12" || sizeKey === "08X10") return "FCANVAS-S";
            if (sizeKey === "12X18" || sizeKey === "12X16") return "FCANVAS-M";
            if (sizeKey === "16X24") return "FCANVAS-L";
        }
        if (typeN.includes("CANVAS") && !typeN.includes("FRAMED")) {
            if (sizeKey === "08X12") return "CANVAS-S";
            if (sizeKey === "12X18") return "CANVAS-M";
            if (sizeKey === "16X24") return "CANVAS-L";
        }
        if (typeN.includes("FLEECE") && typeN.includes("BLANKET")) {
            if (sizeKey === "50X60") return "FLEECE5060";
            if (sizeKey === "60X80") return "FLEECE6080";
        }
        if (typeN.includes("SHERPA") && typeN.includes("BLANKET")) {
            if (sizeKey === "50X60") return "SHERPA5060";
            if (sizeKey === "60X80") return "SHERPA6080";
        }
        return ""; // unknown
    }

    function findVariantBySku(productData, sku) {
        const target = normalizeSku(sku);
        return (productData?.variants || []).find(v => normalizeSku(v.sku) === target) || null;
    }

    // Initialize DOM elements
    function initializeElements() {
        // Tìm button upload - ưu tiên button-general trước
        elements.uploadBtn = document.getElementById("button-general")
        elements.modal = document.getElementById("custom-upload-modal");
        elements.closeBtn = document.getElementById("custom-modal-close");
        elements.browseLink = document.getElementById("custom-browse-link");
        elements.fileInput = document.getElementById("custom-upload-input");
        elements.previewBlock = document.querySelector(".photo-preview-block") || document.getElementById("upload-preview-section");
        elements.previewImg = document.getElementById("photo-preview-img") || document.getElementById("upload-preview-img");
        elements.removeBtn = document.querySelector(".photo-preview-remove") || document.getElementById("remove-upload-btn");
        elements.loadingBlock = document.querySelector(".photo-preview-loading");
        elements.generateBtn = document.querySelector(".custom-generate-btn") || document.getElementById("custom-generate-btn");
        elements.loadingBar = document.querySelector(".photo-loading-bar-inner");
        elements.loadingLabel = document.querySelector(".photo-loading-bar-label");
        elements.dropZone = document.querySelector(".photo-upload-drop");
        elements.modalBody = document.querySelector(".custom-modal-body");
        elements.generatingStep = document.querySelector(".custom-generating-step") || document.getElementById("main-generating-step");
        elements.designSelection = document.getElementById("design-selection");
        elements.designList = document.querySelector(".design-list");
        elements.addToCartBtn = document.getElementById("custom-add-to-cart-btn");
        elements.stepDisplay = document.getElementById("step-display");

        if (elements.generatingStep) {
            elements.generatingBar = elements.generatingStep.querySelector(
                ".photo-loading-bar-inner"
            );
            elements.generatingLabel = elements.generatingStep.querySelector(
                ".photo-loading-bar-label"
            );
        }
    }

    // Modal management
    const modalManager = {
        open() {
            if (elements.modal) {
                elements.modal.style.display = "flex";
                document.body.classList.add("body-no-scroll");
                if (elements.modalBody) {
                    elements.modalBody.style.display = "";
                    var generateBtn = elements.modalBody.querySelector(
                        ".custom-generate-btn"
                    );
                    if (generateBtn) generateBtn.textContent = "Generate Preview";
                }
                if (elements.generatingStep)
                    elements.generatingStep.style.display = "none";
            }
        },

        close() {
            if (elements.modal) {
                elements.modal.style.display = "none";
                document.body.classList.remove("body-no-scroll");
                this.resetPreview();

                // Clean up localStorage if no design selection
                if (
                    !elements.designSelection ||
                    elements.designSelection.style.display === "none"
                ) {
                    utils.storage.remove(CONSTANTS.STORAGE_KEYS.UPLOADED_FILE_NAME);
                }
            }
        },

        resetPreview() {
            if (elements.previewBlock) elements.previewBlock.style.display = "none";
            if (elements.previewImg) {
                elements.previewImg.src = "";
                elements.previewImg.style.filter = "";
            }
            if (elements.loadingBlock) elements.loadingBlock.style.display = "none";
        },
    };

    // File upload handling
    const uploadManager = {
        async uploadFile(file, loraName) {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("loraName", loraName);
            formData.append("options", "2");
            try {
                const nameInput = document.getElementById('kid-message-input');
                if (nameInput && nameInput.value) {
                    const childName = nameInput.value.trim().substring(0, 15);
                    formData.append("childName", childName);
                }
            } catch (_) { }

            try {
                const xhr = new XMLHttpRequest();

                // Show upload progress
                this.showUploadProgress(xhr);

                const response = await new Promise((resolve, reject) => {
                    xhr.open(
                        "POST",
                        CONSTANTS.API_BASE_URL + CONSTANTS.GEMINI_GENERATE_ENDPOINT,
                        true
                    );

                    xhr.onload = () => {
                        if (xhr.status === 200) {
                            try {
                                const data = JSON.parse(xhr.responseText);
                                resolve(data);
                            } catch (e) {
                                reject(new Error("Invalid JSON response"));
                            }
                        } else {
                            reject(new Error(`HTTP ${xhr.status}`));
                        }
                    };

                    xhr.onerror = () => reject(new Error("Network error"));
                    xhr.send(formData);
                });

                if (response.statusCode === 201 && response.data?.taskId && response.data?.images) {
                    // Store taskId and images
                    const newTaskId = response.data.taskId;
                    state.taskId = newTaskId; // Keep current task for backward compatibility

                    // Add to active tasks list
                    utils.addActiveTask(newTaskId);
                    utils.storage.set(CONSTANTS.STORAGE_KEYS.TASK_ID, newTaskId);

                    console.log('✅ Task added to active list:', newTaskId);

                    // Store images for later use - chỉ lưu 1 hình
                    if (response.data.images.length >= 1) {
                        utils.storage.set("generatedImage_" + newTaskId + "_1", response.data.images[0].imageUrl);
                    }

                    // Now that API has responded successfully, animate to 100%
                    const uploadingPercent = elements.previewBlock?.querySelector(
                        ".photo-uploading-percent"
                    );

                    if (uploadingPercent) {
                        // Get current display progress
                        const currentProgress = parseInt(uploadingPercent.textContent) || 0;

                        // Animate to 100% smoothly
                        const finalAnimation = setInterval(() => {
                            const newProgress = Math.min(currentProgress + 2, 100); // Increment by 2% each step
                            uploadingPercent.textContent = newProgress + "%";
                            uploadingPercent.classList.remove("pop");
                            void uploadingPercent.offsetWidth; // force reflow
                            uploadingPercent.classList.add("pop");

                            if (newProgress === 100) {
                                uploadingPercent.classList.add("complete");
                                clearInterval(finalAnimation);

                                // Update button text to "Generate Preview" when reaching 100%
                                if (elements.generateBtn) {
                                    elements.generateBtn.textContent = "Generate Preview";
                                }

                                // Wait 0.7s then hide progress
                                setTimeout(() => {
                                    this.hideUploadProgress();
                                }, 700);
                            }
                        }, 30); // Faster animation to 100% (30ms per step)

                        state.intervals.add(finalAnimation);
                    } else {
                        // Fallback if no progress element
                        await new Promise((res) => setTimeout(res, 700));
                        this.hideUploadProgress();

                        // Update button text immediately for fallback case
                        if (elements.generateBtn) {
                            elements.generateBtn.textContent = "Generate Preview";
                        }
                    }

                    // Store a dummy fileName for compatibility
                    utils.storage.set(
                        CONSTANTS.STORAGE_KEYS.UPLOADED_FILE_NAME,
                        "uploaded_file_" + Date.now()
                    );
                    state.isUploadSuccess = true;

                    // Auto-trigger generation process after successful upload


                    // Close modal and show generating step
                    modalManager.close();

                    // Show generating step
                    const generatingStep = document.getElementById("main-generating-step");
                    if (generatingStep) {
                        generatingStep.style.display = "block";

                    } else {
                        // Fallback: tìm kiếm theo class
                        const altGeneratingStep = document.querySelector(".custom-generating-step");
                        if (altGeneratingStep) {
                            altGeneratingStep.style.display = "flex";

                        } else {

                        }
                    }

                    // Start generation process
                    setTimeout(() => {
                        if (window.generationManager && window.generationManager.generatePreview) {

                            window.generationManager.generatePreview();
                        } else {

                            triggerGenerationFallback();
                        }
                    }, 500); // Small delay to ensure UI is ready

                    return true;
                } else {
                    throw new Error("Upload failed");
                }
            } catch (error) {
                console.error("Upload error:", error);
                state.isUploadSuccess = false;

                utils.showToast("Upload failed. Please try again.");
                this.hideUploadProgress(); // Ẩn progress ngay khi lỗi

                // Reset progress state
                const uploadingPercent = elements.previewBlock?.querySelector(
                    ".photo-uploading-percent"
                );
                if (uploadingPercent) {
                    uploadingPercent.textContent = "0%";
                    uploadingPercent.classList.remove("pop", "complete");
                }

                return false;
            }
        },

        showUploadProgress(xhr) {
            const uploadingProgress = elements.previewBlock?.querySelector(
                ".photo-uploading-progress"
            );
            const uploadingPercent = elements.previewBlock?.querySelector(
                ".photo-uploading-percent"
            );

            if (uploadingProgress) uploadingProgress.style.display = "flex";
            if (uploadingPercent) {
                uploadingPercent.style.display = "block";
                uploadingPercent.textContent = "0%";
                uploadingPercent.classList.remove("pop", "complete");
            }

            // Track actual upload progress
            let actualProgress = 0;
            let displayProgress = 0;
            let progressInterval;

            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                    actualProgress = Math.round((e.loaded / e.total) * 100);

                    // Clear existing interval
                    if (progressInterval) {
                        clearInterval(progressInterval);
                    }

                    // Create smooth progress animation - but only go up to 85% during upload
                    const maxUploadProgress = Math.min(actualProgress, 85);

                    progressInterval = setInterval(() => {
                        if (displayProgress < maxUploadProgress) {
                            displayProgress += 1;

                            if (uploadingPercent) {
                                uploadingPercent.textContent = displayProgress + "%";
                                uploadingPercent.classList.remove("pop");
                                void uploadingPercent.offsetWidth; // force reflow to restart animation
                                uploadingPercent.classList.add("pop");
                            }

                            // Stop at max upload progress, don't go to 100% yet
                            if (displayProgress >= maxUploadProgress) {
                                clearInterval(progressInterval);
                            }
                        }
                    }, 50); // Slower animation - 50ms per step
                }
            };

            // Store interval for cleanup
            state.intervals.add(progressInterval);
        },

        hideUploadProgress() {
            const uploadingProgress = elements.previewBlock?.querySelector(
                ".photo-uploading-progress"
            );
            const uploadingPercent = elements.previewBlock?.querySelector(
                ".photo-uploading-percent"
            );

            if (uploadingProgress) uploadingProgress.style.display = "none";
            if (uploadingPercent) {
                uploadingPercent.style.display = "none";
                uploadingPercent.classList.remove("pop", "complete");
                uploadingPercent.textContent = "0%";
            }

            // Note: Button text will be updated by uploadFile success/error handlers
        },

        handleFileSelect(file) {
            if (!file) return;

            // Get loraName from the upload button or use default
            const loraName = elements.uploadBtn?.getAttribute("data-product-type") || "Pah-custom-0020aa";

            const reader = new FileReader();
            reader.onload = (ev) => {
                if (elements.previewImg) {
                    elements.previewImg.src = ev.target.result;
                    elements.previewImg.style.filter = "";
                }
                if (elements.previewBlock) elements.previewBlock.style.display = "flex";
                if (elements.dropZone) elements.dropZone.style.display = "none";
                if (elements.loadingBlock) elements.loadingBlock.style.display = "none";

                // Upload file with loraName
                this.uploadFile(file, loraName);
            };
            reader.readAsDataURL(file);
        },

        removeFile() {
            modalManager.resetPreview();
            if (elements.fileInput) elements.fileInput.value = "";
            if (elements.dropZone) elements.dropZone.style.display = "";

            utils.storage.remove(CONSTANTS.STORAGE_KEYS.UPLOADED_FILE_NAME);
            if (state.taskId) {
                utils.storage.remove(
                    CONSTANTS.STORAGE_KEYS.SELECTED_VERSION + state.taskId
                );
            }
        },
    };

    // Generation process management
    const generationManager = {
        async generatePreview() {
            // Check if we already have generated images from the upload process
            const taskId = utils.storage.get(CONSTANTS.STORAGE_KEYS.TASK_ID);
            const { img1 } = utils.getTaskImages(taskId);

            if (taskId && img1) {
                this.handleGenerationComplete({
                    image_runninghub_1: img1
                });
                return;
            }

            // Fallback: if no pre-generated images, show error
            utils.showToast("No generated images found. Please upload again.");
            this.handleGenerationError();
        },

        resetProgress() {
            if (elements.generatingBar) {
                elements.generatingBar.style.width = "4%";
            }
            if (elements.generatingLabel) {
                elements.generatingLabel.textContent = "4%";
            }

            // Reset step progress
            this.resetStepProgress();
        },

        resetStepProgress() {
            // Reset to first step
            this.currentStepIndex = 0;
            this.updateStepDisplay(this.currentStepIndex);
        },

        updateStepDisplay(stepIndex) {
            const stepDisplay = document.getElementById('step-display');
            if (!stepDisplay) return;

            const stepNumber = stepDisplay.querySelector('#step-number');
            const stepDescription = stepDisplay.querySelector('#step-description');

            if (stepNumber && stepDescription) {
                stepNumber.textContent = `Step ${stepIndex + 1}:`;
                stepDescription.textContent = CONSTANTS.GENERATION_STEPS[stepIndex];
            }

            // Update time remaining - now positioned below the dotlottie-player
            const timeRemaining = document.getElementById('time-remaining');
            if (timeRemaining) {
                // Faster countdown to reflect 2s steps: 10 -> 8 -> 6 -> 4 -> 2 -> 0
                const remainingSeconds = Math.max(0, 10 - (stepIndex * 2));
                timeRemaining.textContent = remainingSeconds;
            }
        },

        nextStep() {
            if (this.currentStepIndex < CONSTANTS.GENERATION_STEPS.length - 1) {
                this.currentStepIndex++;
                this.updateStepDisplay(this.currentStepIndex);
            }
        },

        startProgressAndSSE(taskId) {
            let percent = 4;
            let stage = 0;
            // Slow down progress to align with 5s step transitions
            const stageTimes = [10000, 7000, 7000];
            const stagePercents = [50, 70, 90];
            let startPercent = 4;
            let endPercent = stagePercents[stage];
            let startTime = Date.now();
            let duration = stageTimes[stage];

            // Initialize step progression
            this.currentStepIndex = 0;
            this.updateStepDisplay(this.currentStepIndex);

            // Start step progression timer (every 5 seconds)
            this.stepProgressionTimer = setInterval(() => {
                this.nextStep();
            }, 5000);

            const updateBar = (p) => {
                if (elements.generatingBar) {
                    elements.generatingBar.style.width = p + "%";
                }
                if (elements.generatingLabel) {
                    elements.generatingLabel.textContent = p + "%";
                }

                // Update step display based on progress
                if (p >= 4 && p < 25) {
                    this.updateStepDisplay(0); // Step 1
                } else if (p >= 25 && p < 45) {
                    this.updateStepDisplay(1); // Step 2
                } else if (p >= 45 && p < 65) {
                    this.updateStepDisplay(2); // Step 3
                } else if (p >= 65 && p < 85) {
                    this.updateStepDisplay(3); // Step 4
                } else if (p >= 85 && p < 95) {
                    this.updateStepDisplay(4); // Step 5
                } else if (p >= 95 && p < 99) {
                    this.updateStepDisplay(5); // Step 6
                } else if (p >= 99) {
                    this.updateStepDisplay(6); // Step 7
                }
            };

            const nextStage = () => {
                stage++;
                if (stage < stageTimes.length) {
                    startPercent = percent;
                    endPercent = stagePercents[stage];
                    startTime = Date.now();
                    duration = stageTimes[stage];
                } else {
                    clearInterval(interval);
                    percent = 99;
                    updateBar(percent);
                }
            };

            const interval = setInterval(() => {
                const now = Date.now();
                const elapsed = now - startTime;

                if (stage < stageTimes.length) {
                    const progress = Math.min(elapsed / duration, 1);
                    percent = Math.round(
                        startPercent + (endPercent - startPercent) * progress
                    );
                    if (percent > endPercent) percent = endPercent;
                    updateBar(percent);
                    if (progress >= 1) nextStage();
                } else {
                    percent = 99;
                    updateBar(percent);
                }
            }, 300);

            state.intervals.add(interval);

            // SSE for status updates
            const sse = new EventSource(
                CONSTANTS.API_BASE_URL + CONSTANTS.STATUS_STREAM_ENDPOINT + "/" + taskId
            );
            state.eventSources.add(sse);

            sse.onmessage = async (event) => {
                try {
                    const res = JSON.parse(event.data);
                    if (res.data?.status === "COMPLETED") {
                        this.handleGenerationComplete(res.data, interval, sse);
                    }
                } catch (e) {
                    console.error("SSE parse error:", e);
                }
            };

            sse.onerror = () => {
                sse.close();
                clearInterval(interval);
                state.intervals.delete(interval);
                state.eventSources.delete(sse);
                this.handleGenerationError();
            };
        },

        async handleGenerationComplete(data, interval, sse) {
            if (interval) clearInterval(interval);
            if (sse) sse.close();
            if (interval) state.intervals.delete(interval);
            if (sse) state.eventSources.delete(sse);

            // Clear step progression timer
            if (this.stepProgressionTimer) {
                clearInterval(this.stepProgressionTimer);
                this.stepProgressionTimer = null;
            }

            const img1 = data.image_runninghub_1;

            if (state.taskId) {
                utils.storage.set("generatedImage_" + state.taskId + "_1", img1);
                // Ensure task is in active list
                utils.addActiveTask(state.taskId);
            }

            if (elements.generatingBar) elements.generatingBar.style.width = "100%";
            if (elements.generatingLabel)
                elements.generatingLabel.textContent = "100%";

            try {
                // Initialize watermark cache for faster processing
                utils.initWatermarkCache();

                // Show loading indicator while processing images
                if (elements.designSelection) {
                    elements.designSelection.style.display = "block";
                    elements.designSelection.innerHTML = `
              <div style="text-align: center; padding: 40px 20px;">
                <div style="width: 40px; height: 40px; border: 4px solid #f3f3f3; border-top: 4px solid #ff2e8a; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 20px;"></div>
                <div style="font-size: 18px; font-weight: 600; color: #333; margin-bottom: 10px;">Processing Images...</div>
                <div style="font-size: 14px; color: #666;">Optimizing and adding watermarks</div>
              </div>
              <style>
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              </style>
            `;
                }

                // Process watermark directly from Cloudinary URL (chỉ 1 hình)
                const watermarked1 = await new Promise((resolve) => {
                    utils.addWatermarkToBase64Optimized(img1, resolve, { fontSize: 12 });
                });

                // Show design selection immediately after processing
                this.showDesignSelection(watermarked1, null, true);

                // Ẩn generating step
                const generatingStep = document.getElementById("main-generating-step");
                if (generatingStep) {
                    generatingStep.style.display = "none";
                } else {
                    // Fallback: tìm kiếm theo class
                    const altGeneratingStep = document.querySelector(".custom-generating-step");
                    if (altGeneratingStep) {
                        altGeneratingStep.style.display = "none";
                    }
                }

                // Hide step display
                const stepDisplay = document.getElementById("step-display");
                if (stepDisplay) {
                    stepDisplay.style.display = "none";
                }

                modalManager.close();

                // Enable button-custom after generation completes
                const buttonCustom = document.querySelector(".button-custom");
                if (buttonCustom) {
                    buttonCustom.style.pointerEvents = "";
                    buttonCustom.style.opacity = "";
                    buttonCustom.style.cursor = "";
                }

                // Show photo-preview-block again after generation completes
                if (elements.previewBlock) {
                    elements.previewBlock.style.display = "flex";
                }

                // Show custom-preview-box again after generation completes
                const customPreviewBox = document.querySelector('.custom-preview-box');
                if (customPreviewBox) {
                    customPreviewBox.style.display = 'block';

                }

                // Enable Shopify Add to Cart buttons after generation completes
                const addToCartButtons = document.querySelectorAll(
                    'button[name="add"], input[name="add"], .product-form button[type="submit"], .product-form__submit'
                );
                addToCartButtons.forEach(btn => {
                    btn.disabled = false;
                    btn.classList.remove("disabled");
                    btn.style.opacity = "";
                    btn.style.cursor = "";

                });

                // Hide upload-preview-section after generation completes (user requested)
                const uploadPreviewSection = document.getElementById("upload-preview-section");
                if (uploadPreviewSection) {
                    uploadPreviewSection.style.display = "none";

                }

                // Scroll to design selection after generation completes
                // Removed duplicate scroll - will be handled by showDesignSelection
                // setTimeout(() => {
                //   if (elements.designSelection) {
                //     
                //     elements.designSelection.scrollIntoView({
                //       behavior: "smooth",
                //       block: "start", // Scroll to top
                //     });
                //   }
                // }, 500);

            } catch (error) {
                console.error("Image processing error:", error);
                this.handleGenerationError();
            }
        },

        handleGenerationError() {
            if (elements.generatingStep) {
                elements.generatingStep.style.display = "none";
            }

            // Hide step display
            const stepDisplay = document.getElementById("step-display");
            if (stepDisplay) {
                stepDisplay.style.display = "none";
            }

            modalManager.close();

            // Clear step progression timer
            if (this.stepProgressionTimer) {
                clearInterval(this.stepProgressionTimer);
                this.stepProgressionTimer = null;
            }

            // Enable button-custom after generation error
            const buttonCustom = document.querySelector(".button-custom");
            if (buttonCustom) {
                buttonCustom.style.pointerEvents = "";
                buttonCustom.style.opacity = "";
                buttonCustom.style.cursor = "";
            }

            // Show custom-preview-box again after generation error
            const customPreviewBox = document.querySelector('.custom-preview-box');
            if (customPreviewBox) {
                customPreviewBox.style.display = 'block';

            }

            // Enable Shopify Add to Cart buttons after generation error
            const addToCartButtons = document.querySelectorAll(
                'button[name="add"], input[name="add"], .product-form button[type="submit"], .product-form__submit'
            );
            addToCartButtons.forEach(btn => {
                btn.disabled = false;
                btn.classList.remove("disabled");
                btn.style.opacity = "";
                btn.style.cursor = "";

            });

            // Show photo-preview-block again after generation error
            if (elements.previewBlock) {
                elements.previewBlock.style.display = "flex";
            }

            // Hide upload-preview-section after generation error (user requested)
            const uploadPreviewSection = document.getElementById("upload-preview-section");
            if (uploadPreviewSection) {
                uploadPreviewSection.style.display = "none";

            }

            this.showNoticeModal();
        },

        showDesignSelection(b64_1, b64_2, forceReload = false) {

            if (!elements.designSelection) return;

            if (forceReload) {
                elements.designSelection.innerHTML = "";

            }

            // Always ensure design selection is visible
            elements.designSelection.style.display = "block";


            // State for selection - Chỉ sử dụng 1 option
            let selectedOption = 1; // Chỉ có 1 option
            let selectedProductType = "Fleece Blanket";
            let selectedImg = b64_1; // Chỉ sử dụng b64_1
            let selectedSize = "50 x 60"; // Default for Fleece Blanket

            // Lưu trữ ảnh đã watermark để tránh gọi lại nhiều lần
            let watermarkedCache = {};

            // Hàm tạo watermark với fontSize responsive và tối ưu hóa
            function addWatermarkResponsive(base64OrUrl, cb) {
                // Check cache first
                if (watermarkedCache[base64OrUrl]) {
                    return cb(watermarkedCache[base64OrUrl]);
                }

                const img = new window.Image();
                img.onload = function () {
                    let fontSize = 12;
                    if (img.width <= 400)
                        fontSize = 18; // mobile nhỏ hơn thì font to hơn chút
                    else if (img.width <= 600) fontSize = 14;
                    else fontSize = 12;

                    // Use optimized watermarking
                    utils.addWatermarkToBase64Optimized(base64OrUrl, (watermarked) => {
                        watermarkedCache[base64OrUrl] = watermarked;
                        cb(watermarked);
                    }, { fontSize });
                };
                img.src = base64OrUrl;
            }

            // Render UI với tối ưu hóa
            function render() {


                // Lấy productData từ window.productData
                var productData = window.productData || {};

                // Find variant by option1 and option2 instead of SKU
                // Prefer matching by SKU derived from product type + size
                var derivedSku = deriveSkuFromSelection(selectedProductType, selectedSize);
                var variant = derivedSku
                    ? findVariantBySku(productData, derivedSku)
                    : (productData.variants || []).find(
                        (v) => normalizeOptionText(v.option1) === normalizeOptionText(selectedProductType) &&
                            normalizeOptionText(v.option2) === normalizeOptionText(selectedSize)
                    );
                var price = variant
                    ? (variant.price / 100).toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                    })
                    : "$0.00";
                var compareAtPrice =
                    variant && variant.compare_at_price
                        ? (variant.compare_at_price / 100).toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                        })
                        : null;

                // Xử lý watermark cho 1 option với tối ưu hóa
                function renderOptionsWithWatermark(wm1, wm2) {


                    // Use requestAnimationFrame for smoother rendering
                    requestAnimationFrame(() => {

                        elements.designSelection.innerHTML = `
                  <div style="font-size:20px;font-weight:600;margin-bottom:10px;text-align: center;">Preview Your Art:</div>
                  <div class="choose-option-flex" style="display:flex;gap:12px;justify-content:center;margin-bottom:24px;width:100%;box-sizing:border-box;flex-wrap: nowrap;">
                    <div class="option-item${selectedOption === 1 ? " selected" : ""}" style="border:2px solid ${selectedOption === 1 ? "#ff2e8a" : "#eee"};border-radius:12px;padding:6px;position:relative;cursor:pointer;max-width:400px;width:100%;box-sizing:border-box;background:#fff;">
                      <img src="${wm1}" alt="Option 1" style="width:100%;height:auto;object-fit:contain;border-radius:10px;display:block;" loading="eager">
                      <button class="zoom-btn" data-src="${wm1}" aria-label="Zoom preview" style="position:absolute;right:10px;bottom:10px;background:#fff;border:1px solid #ddd;border-radius:50%;width:36px;height:36px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.12);cursor:pointer;">
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="11" cy="11" r="6.5" stroke="#000" stroke-width="2"/>
                          <path d="M20 20L16.5 16.5" stroke="#000" stroke-width="2" stroke-linecap="round"/>
                          <path d="M11 8v6M8 11h6" stroke="#000" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                      </button>
                      <div style="text-align:center;padding:8px;font-size:12px;color:#666;font-style:italic;">Watermarks are removed automatically after purchase</div>
                      ${selectedOption === 1 ? '<div style="position:absolute;top:8px;left:8px;background:#ff2e8a;color:#fff;font-size:13px;font-weight:600;padding:2px 10px;border-radius:8px;">Selected</div>' : ""}
                    </div>
                </div>
                
                <style>
                  @media (max-width: 600px) {
                    .choose-option-flex {gap: 8px;}
                    .choose-option-flex .option-item {max-width: 98vw;}
                  }
                </style>
                <div style="font-size:20px;font-weight:600;margin-bottom:10px;text-align: center;">Size Preview ( Inches):</div>
                <div id="mockup-canvas-block" style="margin-bottom: 12px;"></div>
                <div style="font-size:18px;font-weight:600;margin-bottom:8px;">Choose Your Product Type:</div>
                <div style="display:flex;gap:12px;justify-content: center;margin-bottom:10px;flex-wrap:wrap;">
                  <button class="product-type-btn${selectedProductType === "Fleece Blanket" ? " selected" : ""}" style="min-width:160px;height:44px;display:inline-flex;align-items:center;justify-content:center;font-size:16px;font-weight:600;border-radius:8px;padding:0 22px;margin:0;background:#fff;color:#222;border:2px solid #ddd;cursor:pointer;">Fleece Blanket</button>
                  <button class="product-type-btn${selectedProductType === "Sherpa Blanket" ? " selected" : ""}" style="min-width:160px;height:44px;display:inline-flex;align-items:center;justify-content:center;font-size:16px;font-weight:600;border-radius:8px;padding:0 22px;margin:0;background:#fff;color:#222;border:2px solid #ddd;cursor:pointer;">Sherpa Blanket</button>
                </div>
                <div style="font-size:18px;font-weight:600;margin-bottom:8px;">Choose Your Size:</div>
                <div id="choose-size-buttons"></div>
            
                <div style="display:flex;gap:12px;justify-content:center;gap:12px;font-size:28px;font-weight:700;margin-bottom:6px;align-items: center;">
                  <span style="color:#222;">${price}</span>
                  ${compareAtPrice
                                ? `<span style="color:#aaa;font-size:20px;text-decoration:line-through;">${compareAtPrice}</span>`
                                : ""
                            }
                  ${compareAtPrice
                                ? '<span style="background:#ff2e8a;color:#fff;font-size:15px;font-weight:700;padding:2px 10px;border-radius:8px;">SALE</span>'
                                : ""
                            }
                </div>
              `;



                        // Render mockup sau khi set innerHTML với tối ưu hóa
                        const mockupBlock = elements.designSelection.querySelector(
                            "#mockup-canvas-block"
                        );
                        if (selectedProductType === "Fleece Blanket" || selectedProductType === "Sherpa Blanket") {
                            const sizeKey = selectedSize === "50 x 60" ? "50x60" : "60x80";
                            mockupBlock.innerHTML = `<canvas id="blanket-mockup-canvas" width="800" height="800" style="max-width:100%;margin:0 auto;"></canvas>`;
                            setTimeout(
                                () =>
                                    drawMockupBlanketCustom(
                                        selectedImg,
                                        "blanket-mockup-canvas",
                                        sizeKey
                                    ),
                                100
                            );
                        }

                        // Gắn lại sự kiện chọn option
                        elements.designSelection
                            .querySelectorAll(".option-item")
                            .forEach((el, idx) => {
                                el.onclick = function () {
                                    selectedOption = idx + 1;
                                    selectedImg = b64_1; // Chỉ sử dụng b64_1
                                    render();
                                };
                            });

                        // Ensure fallback zoom modal exists only if global opener is not provided by theme
                        (function ensureZoomModal() {
                            if (window.openZoomImage) return; // theme.liquid provides global modal
                            if (document.getElementById('zoom-modal')) return;
                            const modal = document.createElement('div');
                            modal.id = 'zoom-modal';
                            modal.style.cssText = 'position:fixed;inset:0;display:none;align-items:center;justify-content:center;z-index:99999;';
                            modal.innerHTML = '<div id="zoom-backdrop" style="position:absolute;inset:0;background:rgba(0,0,0,0.72);"></div>' +
                                '<div id="zoom-content" style="position:relative;max-width:92vw;max-height:92vh;">' +
                                '  <img id="zoom-image" src="" alt="Zoom Image" style="max-width:92vw;max-height:92vh;border-radius:10px;box-shadow:0 4px 24px rgba(0,0,0,0.35);display:block;" />' +
                                '  <button id="zoom-close" aria-label="Close" style="position:absolute;top:-14px;right:-14px;width:36px;height:36px;border-radius:50%;border:none;background:#fff;box-shadow:0 2px 10px rgba(0,0,0,0.25);cursor:pointer;font-size:18px;">&times;</button>' +
                                '</div>';
                            document.body.appendChild(modal);
                            const hide = () => { modal.style.display = 'none'; };
                            modal.querySelector('#zoom-close')?.addEventListener('click', hide);
                            modal.querySelector('#zoom-backdrop')?.addEventListener('click', hide);
                            document.addEventListener('keydown', function (ev) { if (ev.key === 'Escape') hide(); });
                        })();

                        // Zoom buttons open the image using global theme modal if available
                        elements.designSelection
                            .querySelectorAll('.zoom-btn')
                            .forEach((btn) => {
                                btn.addEventListener('click', function (ev) {
                                    ev.stopPropagation();
                                    var src = btn.getAttribute('data-src');
                                    if (window.openZoomImage) {
                                        try { window.openZoomImage(src); } catch (_) { }
                                        return;
                                    }
                                    // fallback to local modal
                                    var modal = document.getElementById('zoom-modal');
                                    var img = document.getElementById('zoom-image');
                                    if (modal && img && src) {
                                        img.src = src;
                                        modal.style.display = 'flex';
                                    }
                                });
                            });

                        // Product type event
                        elements.designSelection
                            .querySelectorAll(".product-type-btn")
                            .forEach((el, idx) => {
                                const types = ["Fleece Blanket", "Sherpa Blanket"];
                                el.onclick = function () {
                                    selectedProductType = types[idx];
                                    // Default size for each product
                                    if (selectedProductType === "Fleece Blanket") selectedSize = "50 x 60";
                                    else if (selectedProductType === "Sherpa Blanket") selectedSize = "50 x 60";
                                    render();
                                };
                            });

                        // Render size buttons
                        const sizeButtonsDiv = elements.designSelection.querySelector(
                            "#choose-size-buttons"
                        );
                        if (selectedProductType === "Fleece Blanket" || selectedProductType === "Sherpa Blanket") {
                            sizeButtonsDiv.innerHTML = `
                   <div style="display:flex;gap:12px;justify-content:center;margin-bottom:18px;">
                     <button class="size-btn${selectedSize === "50 x 60" ? " selected" : ""}"
                  data-size="50 x 60" style="min-width:120px;height:44px;display:inline-flex;align-items:center;justify-content:center;font-size:16px;font-weight:600;border-radius:8px;padding:0 22px;margin:0;background:#fff;border:2px solid #ddd;color:#222;cursor:pointer;">50 x 60</button>
                     <button class="size-btn${selectedSize === "60 x 80" ? " selected" : ""}"
                  data-size="60 x 80" style="min-width:120px;height:44px;display:inline-flex;align-items:center;justify-content:center;font-size:16px;font-weight:600;border-radius:8px;padding:0 22px;margin:0;background:#fff;border:2px solid #ddd;color:#222;cursor:pointer;">60 x 80</button>
                   </div>`;
                            elements.designSelection
                                .querySelectorAll(".size-btn")
                                .forEach(function (el) {
                                    el.onclick = function () {
                                        selectedSize = el.getAttribute("data-size");
                                        // Save to localStorage for cart to use
                                        utils.storage.set('selectedSize', selectedSize);
                                        render();
                                    };
                                });
                        }

                        // Hide loading indicator after rendering is complete
                        setTimeout(() => {
                            const loadingIndicator = elements.designSelection.querySelector('.design-loading-indicator');
                            if (loadingIndicator) {
                                loadingIndicator.style.display = 'none';
                            }
                        }, 100);

                        if (elements.addToCartBtn) elements.addToCartBtn.style.display = "block";


                    });
                }

                // Start watermarking process immediately
                // REMOVED: Images are already watermarked in handleGenerationComplete

                // Use already watermarked image directly (chỉ 1 hình)
                renderOptionsWithWatermark(b64_1, null);
            }

            // Start rendering process immediately

            render();

            // Auto-scroll từ đầu #design-selection xuống thêm 200px
            setTimeout(() => {
                try {
                    const target = document.getElementById("design-selection") || elements.designSelection;
                    if (target) {
                        const rect = target.getBoundingClientRect();
                        const y = rect.top + window.pageYOffset + 200; // xuống thêm 200px so với đỉnh phần tử
                        window.scrollTo({ top: y, behavior: "smooth" });
                    }
                } catch (err) {
                    // Fallback: nếu có lỗi, vẫn cuộn thêm 200px từ vị trí hiện tại
                    try {
                        window.scrollTo({ top: window.pageYOffset + 200, behavior: "smooth" });
                    } catch (_) { }
                }
            }, 2000); // Delay để chắc chắn UI đã render xong
        },
    };

    // Custom Crop management (replacing Cropper.js)
    const cropperManager = {
        modal: null,
        viewport: null,
        imageToCrop: null,
        cropBox: null,
        handle: null,
        confirmBtn: null,
        cancelBtn: null,

        // State
        imgNaturalW: 0, imgNaturalH: 0,
        imgDisplayW: 0, imgDisplayH: 0,
        imgLeft: 0, imgTop: 0, imgScale: 1,
        dragging: false, resizing: false, zooming: false,
        dragStart: null,
        crop: { x: 40, y: 40, w: 200, h: 300 }, // default 2:3
        ASPECT: 2 / 3, // width/height
        minCropSize: 10, // minimum crop size - reduced to allow very small crops
        maxCropSize: 500, // maximum crop size
        resizeDirection: null, // 'nw', 'ne', 'sw', 'se'

        init() {
            this.modal = document.getElementById('cropper-modal');
            this.imageToCrop = document.getElementById('image-to-crop');
            this.confirmBtn = document.getElementById('confirm-crop-btn');
            this.cancelBtn = document.getElementById('cancel-crop-btn');

            console.log('🔍 Cropper init elements:', {
                modal: !!this.modal,
                imageToCrop: !!this.imageToCrop,
                confirmBtn: !!this.confirmBtn,
                cancelBtn: !!this.cancelBtn
            });

            if (this.confirmBtn) {
                this.confirmBtn.addEventListener('click', this.confirmCrop.bind(this));
            }
            if (this.cancelBtn) {
                this.cancelBtn.addEventListener('click', this.cancelCrop.bind(this));
            }

            this.setupEventListeners();
        },

        setupEventListeners() {
            if (!this.cropBox) return;

            // Dragging crop box
            this.cropBox.addEventListener('pointerdown', (ev) => {
                // Check if clicking on a handle
                if (ev.target.classList.contains('crop-handle')) {
                    ev.preventDefault();
                    this.resizing = true;
                    this.resizeDirection = ev.target.getAttribute('data-direction');
                    this.dragStart = {
                        x: ev.clientX,
                        y: ev.clientY,
                        origX: this.crop.x,
                        origY: this.crop.y,
                        origW: this.crop.w,
                        origH: this.crop.h
                    };
                    ev.target.setPointerCapture(ev.pointerId);
                    return;
                }

                // Otherwise, drag the crop box
                ev.preventDefault();
                this.dragging = true;
                this.dragStart = {
                    x: ev.clientX,
                    y: ev.clientY,
                    origX: this.crop.x,
                    origY: this.crop.y
                };
                this.cropBox.setPointerCapture(ev.pointerId);
            });

            // Mouse wheel zoom
            this.cropBox.addEventListener('wheel', (ev) => {
                ev.preventDefault();
                this.handleZoom(ev.deltaY, ev.clientX, ev.clientY);
            });

            // Touch zoom (pinch)
            let lastTouchDistance = 0;
            this.cropBox.addEventListener('touchstart', (ev) => {
                if (ev.touches.length === 2) {
                    ev.preventDefault();
                    this.zooming = true;
                    const touch1 = ev.touches[0];
                    const touch2 = ev.touches[1];
                    lastTouchDistance = Math.sqrt(
                        Math.pow(touch2.clientX - touch1.clientX, 2) +
                        Math.pow(touch2.clientY - touch1.clientY, 2)
                    );
                }
            });

            this.cropBox.addEventListener('touchmove', (ev) => {
                if (ev.touches.length === 2 && this.zooming) {
                    ev.preventDefault();
                    const touch1 = ev.touches[0];
                    const touch2 = ev.touches[1];
                    const currentDistance = Math.sqrt(
                        Math.pow(touch2.clientX - touch1.clientX, 2) +
                        Math.pow(touch2.clientY - touch1.clientY, 2)
                    );

                    if (lastTouchDistance > 0) {
                        const delta = lastTouchDistance - currentDistance;
                        const centerX = (touch1.clientX + touch2.clientX) / 2;
                        const centerY = (touch1.clientY + touch2.clientY) / 2;
                        this.handleZoom(delta * 10, centerX, centerY);
                    }

                    lastTouchDistance = currentDistance;
                }
            });

            this.cropBox.addEventListener('touchend', (ev) => {
                if (ev.touches.length < 2) {
                    this.zooming = false;
                    lastTouchDistance = 0;
                }
            });

            this.cropBox.addEventListener('pointermove', (ev) => {
                if (this.dragging) {
                    ev.preventDefault();
                    const dx = ev.clientX - this.dragStart.x;
                    const dy = ev.clientY - this.dragStart.y;

                    // Constrain within image display rectangle
                    const minX = this.imgLeft;
                    const minY = this.imgTop;
                    const maxX = this.imgLeft + this.imgDisplayW - this.crop.w;
                    const maxY = this.imgTop + this.imgDisplayH - this.crop.h;

                    // Calculate new position
                    const newX = this.dragStart.origX + dx;
                    const newY = this.dragStart.origY + dy;

                    // Apply bounds constraints
                    this.crop.x = this.clamp(newX, minX, maxX);
                    this.crop.y = this.clamp(newY, minY, maxY);
                    this.updateCropDOM();
                } else if (this.resizing) {
                    ev.preventDefault();
                    this.handleResize(ev.clientX, ev.clientY);
                }
            });

            this.cropBox.addEventListener('pointerup', (ev) => {
                this.dragging = false;
                this.resizing = false;
                this.resizeDirection = null;
                try { this.cropBox.releasePointerCapture(ev.pointerId); } catch (e) { }
            });

        },

        showModal(imageDataUrl) {
            console.log('🔍 showModal called with:', imageDataUrl);
            console.log('🔍 Modal element:', this.modal);
            console.log('🔍 Image element:', this.imageToCrop);

            if (!this.modal || !this.imageToCrop) {
                console.error('❌ Modal or image element not found');
                return;
            }

            // Find elements when modal is shown
            this.viewport = this.modal.querySelector('.cropper-image-container');
            this.cropBox = this.modal.querySelector('.crop-box');

            console.log('🔍 Found elements:', {
                viewport: !!this.viewport,
                cropBox: !!this.cropBox
            });

            if (!this.viewport || !this.cropBox) {
                console.error('Crop elements not found');
                return;
            }

            this.imageToCrop.src = imageDataUrl;
            this.modal.style.display = 'flex';
            this.modal.classList.add('visible');

            console.log('✅ Modal displayed and image src set');

            // Initialize crop after image loads
            this.imageToCrop.onload = () => {
                this.imgNaturalW = this.imageToCrop.naturalWidth;
                this.imgNaturalH = this.imageToCrop.naturalHeight;
                this.setupImageDisplay();

                // Show crop box after setup
                this.cropBox.style.display = 'flex';

                // Ensure all handles are visible
                const handles = this.cropBox.querySelectorAll('.crop-handle');
                console.log('🔍 All handles:', handles);
                handles.forEach((handle, index) => {
                    handle.style.display = 'block';
                    console.log(`Handle ${index}:`, handle, handle.getAttribute('data-direction'));
                });

                console.log('🔍 Handles found:', handles.length);
                console.log('🔍 Crop box HTML:', this.cropBox.innerHTML);

                // Setup event listeners now that elements are available
                this.setupEventListeners();
            };
        },

        setupImageDisplay() {
            if (!this.viewport) {
                console.error('Viewport not found');
                return;
            }

            const vpRect = this.viewport.getBoundingClientRect();
            const maxW = vpRect.width - 20;
            const maxH = vpRect.height - 20;

            const r = Math.min(maxW / this.imgNaturalW, maxH / this.imgNaturalH, 1);
            this.imgScale = r;
            this.imgDisplayW = Math.round(this.imgNaturalW * this.imgScale);
            this.imgDisplayH = Math.round(this.imgNaturalH * this.imgScale);
            this.imgLeft = Math.round((vpRect.width - this.imgDisplayW) / 2);
            this.imgTop = Math.round((vpRect.height - this.imgDisplayH) / 2);

            this.imageToCrop.style.left = this.imgLeft + 'px';
            this.imageToCrop.style.top = this.imgTop + 'px';
            this.imageToCrop.style.width = this.imgDisplayW + 'px';
            this.imageToCrop.style.height = this.imgDisplayH + 'px';

            // Initialize crop box to center with aspect 2:3
            const initialW = Math.min(this.imgDisplayW * 0.5, 300);
            const initialH = Math.round(initialW / this.ASPECT);
            this.crop.w = Math.max(initialW, this.minCropSize);
            this.crop.h = Math.round(this.crop.w / this.ASPECT);
            this.crop.x = this.imgLeft + Math.round((this.imgDisplayW - this.crop.w) / 2);
            this.crop.y = this.imgTop + Math.round((this.imgDisplayH - this.crop.h) / 2);

            this.updateCropDOM();
        },

        updateCropDOM() {
            if (!this.cropBox) return;
            this.cropBox.style.left = this.crop.x + 'px';
            this.cropBox.style.top = this.crop.y + 'px';
            this.cropBox.style.width = this.crop.w + 'px';
            this.cropBox.style.height = this.crop.h + 'px';
        },

        hideModal() {
            if (this.modal) {
                this.modal.classList.remove('visible');
                setTimeout(() => {
                    this.modal.style.display = 'none';
                }, 300);
            }

            // Hide crop box
            if (this.cropBox) {
                this.cropBox.style.display = 'none';
            }
        },

        confirmCrop() {
            if (!this.imageToCrop.src) {
                utils.showToast('No image to crop');
                return;
            }

            // Calculate source rectangle on natural image
            const sx = Math.round((this.crop.x - this.imgLeft) / this.imgScale);
            const sy = Math.round((this.crop.y - this.imgTop) / this.imgScale);
            const sw = Math.round(this.crop.w / this.imgScale);
            const sh = Math.round(this.crop.h / this.imgScale);

            // Create canvas with limited size for performance
            const MAX_H = 600;
            const scale = (sh > MAX_H) ? (MAX_H / sh) : 1;
            const outW = Math.round(sw * scale);
            const outH = Math.round(sh * scale);

            const canvas = document.createElement('canvas');
            canvas.width = outW;
            canvas.height = outH;
            const ctx = canvas.getContext('2d');

            ctx.fillStyle = "#fff";
            ctx.fillRect(0, 0, outW, outH);
            ctx.drawImage(this.imageToCrop, sx, sy, sw, sh, 0, 0, outW, outH);

            // Convert to file and process through the existing upload flow
            canvas.toBlob((blob) => {
                if (blob) {
                    const file = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' });

                    const dataTransfer = new DataTransfer();
                    dataTransfer.items.add(file);

                    const fileInput = document.getElementById('custom-upload-input');
                    if (fileInput) {
                        fileInput.files = dataTransfer.files;

                        this.hideModal();

                        if (window.processFileUpload && typeof window.processFileUpload === 'function') {
                            window.processFileUpload();
                        } else {
                            uploadManager.handleFileSelect(file);
                        }
                    }
                }
            }, 'image/jpeg', 0.8);
        },

        cancelCrop() {
            this.hideModal();
            if (elements.fileInput) {
                elements.fileInput.value = '';
            }
        },

        // Handle resize from 4 corners
        handleResize(clientX, clientY) {
            if (!this.resizeDirection || !this.dragStart) return;

            const dx = clientX - this.dragStart.x;
            const dy = clientY - this.dragStart.y;

            let newX = this.dragStart.origX;
            let newY = this.dragStart.origY;
            let newW = this.dragStart.origW;
            let newH = this.dragStart.origH;

            // Calculate new dimensions based on resize direction
            switch (this.resizeDirection) {
                case 'nw': // Top-left
                    newX = this.dragStart.origX + dx;
                    newY = this.dragStart.origY + dy;
                    newW = this.dragStart.origW - dx;
                    newH = this.dragStart.origH - dy;
                    break;
                case 'ne': // Top-right
                    newY = this.dragStart.origY + dy;
                    newW = this.dragStart.origW + dx;
                    newH = this.dragStart.origH - dy;
                    break;
                case 'sw': // Bottom-left
                    newX = this.dragStart.origX + dx;
                    newW = this.dragStart.origW - dx;
                    newH = this.dragStart.origH + dy;
                    break;
                case 'se': // Bottom-right
                    newW = this.dragStart.origW + dx;
                    newH = this.dragStart.origH + dy;
                    break;
            }

            // Constrain within image bounds BEFORE aspect ratio calculation
            const minX = this.imgLeft;
            const minY = this.imgTop;
            const maxX = this.imgLeft + this.imgDisplayW;
            const maxY = this.imgTop + this.imgDisplayH;

            // Ensure crop box stays within image bounds (no minimum size restriction)
            const maxAllowedW = maxX - minX;
            const maxAllowedH = maxY - minY;

            newX = this.clamp(newX, minX, maxX - Math.max(newW, this.minCropSize));
            newY = this.clamp(newY, minY, maxY - Math.max(newH, this.minCropSize));
            newW = this.clamp(newW, this.minCropSize, maxAllowedW);
            newH = this.clamp(newH, this.minCropSize, maxAllowedH);

            // Maintain aspect ratio while respecting bounds
            const aspectRatio = this.ASPECT;

            // Calculate aspect-ratio constrained dimensions
            let constrainedW = newW;
            let constrainedH = newH;

            if (Math.abs(dx) > Math.abs(dy)) {
                // Width change is dominant
                constrainedH = Math.round(newW / aspectRatio);
                if (this.resizeDirection.includes('n')) {
                    newY = this.dragStart.origY + this.dragStart.origH - constrainedH;
                }
            } else {
                // Height change is dominant
                constrainedW = Math.round(newH * aspectRatio);
                if (this.resizeDirection.includes('w')) {
                    newX = this.dragStart.origX + this.dragStart.origW - constrainedW;
                }
            }

            // Apply size constraints (allow very small crops)
            constrainedW = this.clamp(constrainedW, this.minCropSize, this.maxCropSize);
            constrainedH = Math.round(constrainedW / aspectRatio);

            // Ensure it fits within image bounds
            if (constrainedW > maxAllowedW) {
                constrainedW = maxAllowedW;
                constrainedH = Math.round(constrainedW / aspectRatio);
            }
            if (constrainedH > maxAllowedH) {
                constrainedH = maxAllowedH;
                constrainedW = Math.round(constrainedH * aspectRatio);
            }

            // Final bounds check - ensure the crop box fits within image
            const finalMaxX = maxX - constrainedW;
            const finalMaxY = maxY - constrainedH;

            // Adjust position if needed to keep within bounds
            if (this.resizeDirection.includes('n')) {
                newY = this.dragStart.origY + this.dragStart.origH - constrainedH;
            }
            if (this.resizeDirection.includes('w')) {
                newX = this.dragStart.origX + this.dragStart.origW - constrainedW;
            }

            // Final position constraints
            newX = this.clamp(newX, minX, finalMaxX);
            newY = this.clamp(newY, minY, finalMaxY);

            // Use constrained dimensions
            newW = constrainedW;
            newH = constrainedH;

            // Update crop dimensions
            this.crop.x = newX;
            this.crop.y = newY;
            this.crop.w = newW;
            this.crop.h = newH;

            this.updateCropDOM();
        },

        // Handle zoom functionality
        handleZoom(delta, centerX, centerY) {
            if (!this.viewport) return;

            const zoomFactor = delta > 0 ? 0.9 : 1.1; // Zoom out if delta > 0, zoom in if delta < 0
            const newWidth = this.crop.w * zoomFactor;
            const newHeight = Math.round(newWidth / this.ASPECT);

            // Check size constraints
            if (newWidth < this.minCropSize || newWidth > this.maxCropSize) {
                return;
            }

            // Get image bounds (not viewport bounds)
            const minX = this.imgLeft;
            const minY = this.imgTop;
            const maxX = this.imgLeft + this.imgDisplayW;
            const maxY = this.imgTop + this.imgDisplayH;

            // Check if new size would fit within image bounds
            if (newWidth > (maxX - minX) || newHeight > (maxY - minY)) {
                return;
            }

            // Calculate new position to keep center point fixed
            const vpRect = this.viewport.getBoundingClientRect();
            const centerRelativeX = centerX - vpRect.left;
            const centerRelativeY = centerY - vpRect.top;

            const newX = centerRelativeX - newWidth / 2;
            const newY = centerRelativeY - newHeight / 2;

            // Constrain to image bounds
            const constrainedX = this.clamp(newX, minX, maxX - newWidth);
            const constrainedY = this.clamp(newY, minY, maxY - newHeight);

            // Update crop dimensions
            this.crop.w = newWidth;
            this.crop.h = newHeight;
            this.crop.x = constrainedX;
            this.crop.y = constrainedY;

            this.updateCropDOM();
        },

        // Utility function
        clamp(v, a, b) {
            return Math.max(a, Math.min(b, v));
        }
    };

    // Canvas mockup management
    const canvasManager = {
        init() {
            // Ensure each design wrapper has a canvas
            document.querySelectorAll(".design-img-mockup-wrap").forEach((wrap) => {
                if (!wrap.querySelector("#myCanvas")) {
                    const canvas = document.createElement("canvas");
                    canvas.id = "myCanvas";
                    canvas.width = 600;
                    canvas.height = 450;
                    canvas.style.display = "none";
                    canvas.style.maxWidth = "100%";
                    canvas.style.margin = "0 auto";
                    wrap.appendChild(canvas);
                }
            });
        },

        updateCanvasWithVariantSize(variantSize, canvas, img) {
            if (!canvas || !img) return;
            const ctx = canvas.getContext("2d");

            if (variantSize === "digital") {
                canvas.style.display = "none";
                img.style.display = "";
                return;
            }

            if (!state.backgroundImage) {
                state.backgroundImage = new Image();
                state.backgroundImage.src = CONSTANTS.BACKGROUND_IMAGE_URL;
                state.backgroundImage.onload = () =>
                    this.updateCanvasWithVariantSize(variantSize, canvas, img);
                state.backgroundImage.onerror = () =>
                    console.error("Failed to load background image");
                return;
            }

            const overlayImage = new Image();
            overlayImage.src = img.src;
            overlayImage.onload = () =>
                this.drawCanvas(ctx, variantSize, overlayImage, canvas);
            overlayImage.onerror = () =>
                console.error("Failed to load overlay image");
        },

        drawCanvas(ctx, variantSize, overlayImage, canvas) {
            if (!state.backgroundImage.complete || !overlayImage.complete) {
                setTimeout(
                    () => this.drawCanvas(ctx, variantSize, overlayImage, canvas),
                    100
                );
                return;
            }

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(state.backgroundImage, 0, 0, canvas.width, canvas.height);

            const frame = this.getFrameDimensions(variantSize, canvas);
            const adjustedCoords = this.calculateImagePosition(overlayImage, frame);

            ctx.drawImage(
                overlayImage,
                adjustedCoords.x,
                adjustedCoords.y,
                adjustedCoords.width,
                adjustedCoords.height
            );

            // Add preview text
            ctx.font = "16px Arial";
            ctx.fillStyle = "rgba(255, 255, 255, 0.5)";
            ctx.textAlign = "center";
            ctx.fillText("PREVIEW ONLY", canvas.width / 2, canvas.height - 20);
        },

        getFrameDimensions(variantSize, canvas) {
            const dimensions = {
                "CANVAS-S": { width: 0.22, height: 0.34, x: 0.4, y: 0.3 },
                "CANVAS-M": { width: 0.4, height: 0.6, x: 0.3, y: 0.2 },
                "CANVAS-L": { width: 0.45, height: 0.75, x: 0.25, y: 0.1 },
            };

            const dim = dimensions[variantSize] || dimensions["CANVAS-S"];
            return {
                width: canvas.width * dim.width,
                height: canvas.height * dim.height,
                x: canvas.width * dim.x,
                y: canvas.height * dim.y,
            };
        },

        calculateImagePosition(overlayImage, frame) {
            const frameRatio = frame.width / frame.height;
            const imageRatio = overlayImage.width / overlayImage.height;

            let finalWidth =
                imageRatio > frameRatio ? frame.width : frame.height * imageRatio;
            let finalHeight =
                imageRatio > frameRatio ? frame.width / imageRatio : frame.height;

            return {
                x: frame.x + (frame.width - finalWidth) / 2,
                y: frame.y + (frame.height - finalHeight) / 2,
                width: finalWidth,
                height: finalHeight,
            };
        },
    };

    // Notice modal management
    const noticeModalManager = {
        show({
            icon = `<svg width="60" height="60" aria-hidden="true"><circle cx="30" cy="30" r="28" fill="#fff" stroke="#4ade80" stroke-width="4"/><text x="30" y="40" text-anchor="middle" font-size="40" fill="#4ade80">!</text></svg>`,
            title = "Notice",
            message = "Hang tight—your image is almost ready!<br><br>Do not exit; just 10 more seconds to see your preview.",
            okText = "Ok",
            showExit = true,
        } = {}) {
            let modal = document.getElementById("notice-modal");
            if (!modal) {
                this.createNoticeModal({ icon, title, message, okText, showExit });
                modal = document.getElementById("notice-modal");
            } else {
                // Update content if modal exists
                const iconDiv = modal.querySelector(".notice-modal-icon");
                if (iconDiv) iconDiv.innerHTML = icon;
                const titleDiv = modal.querySelector(".notice-modal-title");
                if (titleDiv) titleDiv.textContent = title;
                const msgDiv = modal.querySelector(".notice-modal-message");
                if (msgDiv) msgDiv.innerHTML = message;
                const okBtn = modal.querySelector("#notice-modal-ok");
                if (okBtn) okBtn.textContent = okText;
                const exitBtn = modal.querySelector("#notice-modal-exit");
                if (exitBtn) exitBtn.style.display = showExit ? "" : "none";
            }
            modal.style.display = "flex";
        },

        createNoticeModal({ icon, title, message, okText, showExit }) {
            // Add CSS if not exists
            if (!document.getElementById("notice-modal-style")) {
                const style = document.createElement("style");
                style.id = "notice-modal-style";
                style.innerHTML = `
            .notice-modal {position:fixed;z-index:99999;left:0;top:0;width:100vw;height:100vh;display:flex;align-items:center;justify-content:center;}
            .notice-modal-overlay {position:absolute;left:0;top:0;width:100vw;height:100vh;background:rgba(0,0,0,0.6);backdrop-filter: blur(4px);-webkit-backdrop-filter: blur(4px); z-index: 1;}
            .notice-modal-content {position:relative;background:#fff;border-radius:12px;max-width:340px;width:90vw;padding:32px 18px 18px 18px;box-shadow:0 2px 16px rgba(0,0,0,0.13);text-align:center;z-index:2;}
            .notice-modal-close {position:absolute;top:10px;right:16px;cursor:pointer;font-size:22px;color:#888;background:#f3f3f3;border-radius:50%;width:32px;height:32px;display:flex;align-items:center;justify-content:center;font-weight:bold;}
            .notice-modal-icon {margin-bottom:18px;}
            .notice-modal-title {font-size:32px;font-weight:600;margin-bottom:8px;}
            .notice-modal-message {color:#444;margin-bottom:18px;font-size:16px;}
            .notice-modal-buttons {display:flex;gap:12px;margin-top:8px;justify-content:center;}
            .notice-modal-ok {flex:1;background:#222;color:#fff;border:none;border-radius:8px;padding:12px 0;font-size:18px;font-weight:500;cursor:pointer;transition:background 0.18s;}
            .notice-modal-ok:hover {background:#444;}
            .notice-modal-exit {flex:1;background:#f3f3f3;color:#666;border:none;border-radius:8px;padding:12px 0;font-size:18px;font-weight:500;cursor:pointer;transition:background 0.18s;}
            .notice-modal-exit:hover {background:#e0e0e0;}
          `;
                document.head.appendChild(style);
            }

            const modal = document.createElement("div");
            modal.id = "notice-modal";
            modal.className = "notice-modal";
            modal.setAttribute("role", "dialog");
            modal.setAttribute("aria-modal", "true");
            modal.setAttribute("aria-labelledby", "notice-modal-title");
            modal.innerHTML = `
          <div class="notice-modal-overlay">&times;</div>
          <div class="notice-modal-content">
            <span class="notice-modal-close" id="notice-modal-close" aria-label="Close">&times;</span>
            <div class="notice-modal-icon">${icon}</div>
            <div class="notice-modal-title" id="notice-modal-title">${title}</div>
            <div class="notice-modal-message">${message}</div>
            <div class="notice-modal-buttons">
              <button class="notice-modal-exit" id="notice-modal-exit" style="${showExit ? "" : "display:none;"
                }">Exit</button>
              <button class="notice-modal-ok" id="notice-modal-ok">${okText}</button>
            </div>
          </div>
        `;

            document.body.appendChild(modal);
            this.bindNoticeModalEvents(modal);
        },

        bindNoticeModalEvents(modal) {
            const closeBtn = modal.querySelector("#notice-modal-close");
            const okBtn = modal.querySelector("#notice-modal-ok");
            const exitBtn = modal.querySelector("#notice-modal-exit");
            const overlay = modal.querySelector(".notice-modal-overlay");

            const closeModal = () => modal.remove();

            closeBtn?.addEventListener("click", closeModal);
            okBtn?.addEventListener("click", closeModal);
            exitBtn?.addEventListener("click", () => {
                closeModal();
                window.location.reload();
            });
            overlay?.addEventListener("click", closeModal);
        },
    };

    // expose globally for custom call
    window.noticeModalManager = noticeModalManager;

    // Event handlers
    const eventHandlers = {
        // Context menu prevention
        handleContextMenu(e) {
            if (
                e.target.tagName === "IMG" ||
                e.target.tagName === "CANVAS" ||
                e.target.classList.contains("design-img-mockup-wrap") ||
                e.target.closest(".design-img-mockup-wrap")
            ) {
                e.preventDefault();
            }
        },

        // Before unload cleanup
        handleBeforeUnload() {
            if (
                !elements.designSelection ||
                elements.designSelection.style.display === "none"
            ) {
                utils.storage.remove(CONSTANTS.STORAGE_KEYS.UPLOADED_FILE_NAME);
            }
        },

        // File input change
        handleFileInputChange(e) {
            const file = e.target.files?.[0];
            if (file) {
                uploadManager.handleFileSelect(file);
            }
        },

        // Drop zone events
        handleDropZoneClick(e) {
            if (
                e.target === elements.fileInput ||
                e.target.id === "custom-browse-link"
            )
                return;
            elements.fileInput?.click();
        },

        handleDragOver(e) {
            e.preventDefault();
            if (elements.dropZone) elements.dropZone.style.background = "#e0ffe0";
        },

        handleDragLeave() {
            if (elements.dropZone) elements.dropZone.style.background = "#fafafa";
        },

        handleDrop(e) {
            e.preventDefault();
            if (elements.dropZone) elements.dropZone.style.background = "#fafafa";

            const files = e.dataTransfer.files;
            if (files.length && elements.fileInput) {
                elements.fileInput.files = files;
                elements.fileInput.dispatchEvent(new Event("change"));
            }
        },

        // Design selection
        handleDesignClick(e) {
            const item = e.target.closest(".design-item");
            if (!item) return;

            // Remove previous selection
            document.querySelectorAll(".design-item").forEach((el) => {
                el.classList.remove("selected");
                const btn = el.querySelector(".design-selected-btn");
                if (btn) btn.style.display = "none";
            });

            // Set new selection
            item.classList.add("selected");
            const btn = item.querySelector(".design-selected-btn");
            if (btn) btn.style.display = "block";

            // Save version
            const version =
                Array.from(
                    elements.designList?.querySelectorAll(".design-item") || []
                ).indexOf(item) + 1;
            if (state.taskId) {
                state.selectedVersion = version;
                utils.storage.set(
                    CONSTANTS.STORAGE_KEYS.SELECTED_VERSION + state.taskId,
                    version
                );
            }

            // Scroll to selected design
            setTimeout(() => {
                item.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 300);
        },

        // Variant change
        handleVariantChange(e) {
            if (
                !e.target.matches(
                    'input[name="id"], input[name="variant"], input[data-option-value-id], select[name="id"], select[name="variant"]'
                )
            ) {
                return;
            }

            const selectedValue = e.target.value;
            const productData = this.getProductData();

            if (!productData?.variants) return;

            // If the selector returns a label with emoji/symbols, still map to SKU groups
            let variant = null;
            try {
                const label = normalizeOptionText(selectedValue);
                let skuGroup = null;
                if (label.includes("DIGITAL")) skuGroup = "DIGITAL";
                else if (label.includes("FRAMED") && label.includes("CANVAS")) skuGroup = "FRAMED_CANVAS";
                else if (label.includes("CANVAS")) skuGroup = "CANVAS";

                if (skuGroup === "DIGITAL") {
                    variant = (productData.variants || []).find(v => normalizeSku(v.sku) === "DIGITAL");
                } else if (skuGroup === "FRAMED_CANVAS") {
                    variant = (productData.variants || []).find(v => normalizeSku(v.sku).startsWith("FCANVAS-"));
                } else if (skuGroup === "CANVAS") {
                    variant = (productData.variants || []).find(v => normalizeSku(v.sku).startsWith("CANVAS-"));
                }
            } catch (_) {
                // fallback to option1 compare
                variant = productData.variants.find(
                    (v) => normalizeOptionText(v.option1) === normalizeOptionText(selectedValue)
                );
            }
            if (!variant?.sku) return;

            const sku = variant.sku.toUpperCase().trim();
            let variantSize = null;

            if (sku === "DIGITAL") variantSize = "digital";
            else if (sku === "CANVAS-S") variantSize = "CANVAS-S";
            else if (sku === "CANVAS-M") variantSize = "CANVAS-M";
            else if (sku === "CANVAS-L") variantSize = "CANVAS-L";

            // Update all design items
            document.querySelectorAll(".design-item").forEach((item) => {
                const wrap = item.querySelector(".design-img-mockup-wrap");
                const img = wrap?.querySelector("img");
                const canvas = wrap?.querySelector(".design-mockup-canvas");

                if (variantSize && canvas && img) {
                    wrap.classList.add("mockup-canvas");
                    img.style.display = "none";
                    canvas.style.display = "block";
                    canvasManager.updateCanvasWithVariantSize(variantSize, canvas, img);
                } else if (wrap && img && canvas) {
                    wrap.classList.remove("mockup-canvas");
                    img.style.display = "";
                    canvas.style.display = "none";
                }
            });

            // Scroll to selected design
            setTimeout(() => {
                const selectedDesign = document.querySelector(".design-item.selected");
                if (selectedDesign) {
                    selectedDesign.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                }
            }, 300);
        },

        getProductData() {
            const productJsonScript = document.querySelector("[data-product-json]");
            if (productJsonScript) {
                try {
                    return JSON.parse(productJsonScript.textContent);
                } catch (e) {
                    console.error("Error parsing product JSON:", e);
                }
            }
            return null;
        },

        // Add to cart with version confirmation
        async handleAddToCart(e) {
            // Check if template is active
            if (!isTemplateActive()) {

                return;
            }

            if (
                !e.target.matches(
                    'button[name="add"], input[name="add"], .product-form button[type="submit"]'
                )
            ) {
                return;
            }

            const form = e.target.closest("form");


            // Check multiple form selectors
            if (
                !form ||
                (!form.matches(".product-form form") &&
                    !form.matches(".product-form") &&
                    !form.matches('form[action*="/cart/add"]'))
            ) {

                return;
            }

            // Get selected variant from design selection instead of form
            const productData = this.getProductData();
            let selectedVariant = null;
            let selectedVariantId = null;

            // Check if design selection is visible and get selected options
            const designSelection = document.getElementById("design-selection");
            if (
                designSelection &&
                window.getComputedStyle(designSelection).display !== "none"
            ) {
                // Get selected product type and size from design selection
                const selectedProductTypeBtn = designSelection.querySelector(
                    ".product-type-btn.selected"
                );
                const selectedSizeBtn =
                    designSelection.querySelector(".size-btn.selected");

                if (selectedProductTypeBtn && selectedSizeBtn) {
                    const selectedProductType = selectedProductTypeBtn.textContent.trim();
                    const selectedSize = selectedSizeBtn.getAttribute("data-size");


                    // Find variant by option1 and option2
                    if (productData?.variants) {
                        const derivedSku2 = deriveSkuFromSelection(selectedProductType, selectedSize);
                        selectedVariant = derivedSku2
                            ? findVariantBySku(productData, derivedSku2)
                            : productData.variants.find(
                                (v) => normalizeOptionText(v.option1) === normalizeOptionText(selectedProductType) &&
                                    normalizeOptionText(v.option2) === normalizeOptionText(selectedSize)
                            );
                        if (selectedVariant) {
                            selectedVariantId = selectedVariant.id;
                        }
                    }
                }
            }

            // Fallback to form input if no design selection
            if (!selectedVariant) {
                const variantInput = form.querySelector('input[name="id"]');
                selectedVariantId = variantInput
                    ? parseInt(variantInput.value, 10)
                    : null;
                if (productData?.variants && selectedVariantId) {
                    selectedVariant = productData.variants.find(
                        (v) => v.id === selectedVariantId
                    );
                }

            }

            // Handle custom logic with taskId
            const taskId = utils.storage.get(CONSTANTS.STORAGE_KEYS.TASK_ID);

            if (taskId) {
                e.preventDefault();
                e.stopPropagation();

                // Check if task is still active (fallback to old logic if no activeTasks)
                const activeTasks = utils.getActiveTasks();
                const hasActiveTasks = activeTasks.length > 0;

                console.log('🔍 Checking task validity:', { taskId, activeTasks, hasActiveTasks });

                if (hasActiveTasks && !activeTasks.includes(taskId)) {
                    console.warn('❌ Task not found in active list:', taskId);
                    utils.showToast("This image session has expired. Please upload again.");
                    return;
                }

                // If no activeTasks system, check if images exist (backward compatibility)
                if (!hasActiveTasks) {
                    const { img1 } = utils.getTaskImages(taskId);
                    console.log('🔍 Fallback check - images exist:', { taskId, hasImages: !!img1 });
                    if (!img1) {
                        console.warn('❌ No images found for task:', taskId);
                        utils.showToast("This image session has expired. Please upload again.");
                        return;
                    }
                }

                console.log('✅ Task validation passed:', taskId);

                // Get selected version and image URL (chỉ 1 hình)
                const version = 1; // Chỉ có 1 version
                const { img1 } = utils.getTaskImages(taskId);

                // Save cart item data to localStorage before adding to cart
                const cartItemData = {
                    taskId: taskId,
                    version: version,
                    imageUrl: img1,
                    productType: selectedVariant?.option1 || 'Unknown',
                    size: selectedVariant?.option2 || 'Unknown',
                    variantId: selectedVariantId,
                    timestamp: Date.now(),
                    productTitle: productData?.title || 'Custom Product'
                };

                // Save to cart items array
                this.saveCartItemData(cartItemData);

                try {
                    console.log('🔄 Calling version API:', { taskId, version, endpoint: CONSTANTS.API_BASE_URL + CONSTANTS.VERSION_ENDPOINT + "/" + taskId });

                    const response = await fetch(
                        CONSTANTS.API_BASE_URL + CONSTANTS.VERSION_ENDPOINT + "/" + taskId,
                        {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ version }),
                        }
                    );

                    console.log('📡 Version API response:', { status: response.status, ok: response.ok });

                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}`);
                    }

                    const result = await response.json();
                    console.log('✅ Version API success:', result);

                } catch (err) {
                    console.error("❌ Version API error:", err);
                    utils.showToast(
                        "Error: Cannot confirm image version. Please try again!"
                    );
                    return;
                }

                // Update form variant input if we found the correct variant
                if (selectedVariantId) {
                    const variantInput = form.querySelector('input[name="id"]');
                    if (variantInput) {
                        const oldValue = variantInput.value;
                        variantInput.value = selectedVariantId;
                        console.log('🔄 Updated variant input:', { oldValue, newValue: selectedVariantId });
                    } else {
                        console.warn('⚠️ No variant input found');
                    }
                }

                const quantityInput = form.querySelector('input[name="quantity"]');
                // Đảm bảo quantity = 1
                if (quantityInput) {
                    quantityInput.value = "1";
                    console.log('🔄 Set quantity to 1');
                }

                // Inject image_id
                const oldInput = form.querySelector('input[name="properties[image_id]"]');
                if (oldInput) {
                    oldInput.remove();
                    console.log('🗑️ Removed old image_id input');
                }

                const input = document.createElement("input");
                input.type = "hidden";
                input.name = "properties[image_id]";
                input.value = taskId;
                form.appendChild(input);
                console.log('➕ Added image_id input:', taskId);

                console.log('🚀 About to submit form...');
                setTimeout(() => {
                    // Don't cleanup task data immediately - let cart page handle it
                    // utils.cleanupTask(taskId);
                    // utils.storage.remove(CONSTANTS.STORAGE_KEYS.UPLOADED_FILE_NAME);

                    console.log('📤 Dispatching form submit event...');
                    // Dispatch lại sự kiện submit để các script analytics vẫn bắt được
                    form.dispatchEvent(
                        new Event("submit", { cancelable: true, bubbles: true })
                    );

                    // Also try direct form submission as fallback
                    try {
                        form.submit();
                        console.log('✅ Form submitted successfully');
                    } catch (submitErr) {
                        console.error('❌ Form submit failed:', submitErr);
                    }
                }, 300);
                return;
            } else {

            }

        },

        // Save cart item data to localStorage
        saveCartItemData(cartItemData) {
            try {
                const existingCartItems = JSON.parse(utils.storage.get('cartItems') || '[]');

                // Remove any existing item with same taskId to avoid duplicates
                const filteredItems = existingCartItems.filter(item => item.taskId !== cartItemData.taskId);

                // Add new item
                filteredItems.push(cartItemData);

                // Save back to localStorage
                utils.storage.set('cartItems', JSON.stringify(filteredItems));

                console.log('💾 Saved cart item data:', cartItemData);
                console.log('📦 Total cart items:', filteredItems.length);
            } catch (error) {
                console.error('❌ Error saving cart item data:', error);
            }
        },

        // Get cart item data by taskId
        getCartItemData(taskId) {
            try {
                const cartItems = JSON.parse(utils.storage.get('cartItems') || '[]');
                return cartItems.find(item => item.taskId === taskId);
            } catch (error) {
                console.error('❌ Error getting cart item data:', error);
                return null;
            }
        },

        // Clean up cart item data
        cleanupCartItemData(taskId) {
            try {
                const existingCartItems = JSON.parse(utils.storage.get('cartItems') || '[]');
                const filteredItems = existingCartItems.filter(item => item.taskId !== taskId);
                utils.storage.set('cartItems', JSON.stringify(filteredItems));
                console.log('🧹 Cleaned up cart item data for taskId:', taskId);
            } catch (error) {
                console.error('❌ Error cleaning up cart item data:', error);
            }
        },

        // Debug function to show all cart items
        showCartItems() {
            try {
                const cartItems = JSON.parse(utils.storage.get('cartItems') || '[]');
                console.log('🛒 Cart Items in localStorage:', cartItems);
                return cartItems;
            } catch (error) {
                console.error('❌ Error getting cart items:', error);
                return [];
            }
        },


    };

    // Check if this template is active
    function isTemplateActive() {
        // Check if this template is being used - try multiple selectors
        const templateElement = document.querySelector('[data-section*="main-product"]') ||
            document.querySelector('product-info') ||
            document.querySelector('#MainProduct');

        // Check for specific template elements
        const hasCustomUploadBtn = document.querySelector('.custom-upload-btn');
        const hasDesignSelection = document.getElementById('design-selection');
        const hasProductInfo = document.querySelector('product-info');

        // Detect template name based on elements present
        let templateName = "Unknown";
        let isActive = false;

        if (hasProductInfo) {
            // Check if it's mytemplate by looking for specific elements
            if (hasDesignSelection) {
                templateName = "main-product";
                isActive = true;
            } else {
                templateName = "main-product";
                isActive = true;
            }
        }

        // Additional check: if we're on a product page and have custom elements
        if (window.location.pathname.includes('/products/')) {
            if (hasDesignSelection || hasCustomUploadBtn) {
                templateName = "main-product";
                isActive = true;
            } else if (hasProductInfo) {
                templateName = "main-product";
                isActive = true;
            }
        }

        // For default product template, always activate if on product page
        if (window.location.pathname.includes('/products/') && hasProductInfo) {
            if (!isActive) {
                templateName = "main-product";
                isActive = true;
            }
        }

        // Check current page template
        const currentTemplate = window.location.pathname.includes('/products/') ? 'product' : 'other';
        const templateFile = document.querySelector('script[src*="custom-template.js"]') ? 'custom-template.js' :
            document.querySelector('script[src*="custom-product-form.js"]') ? 'custom-product-form.js' : 'none';

        return isActive;
    }

    // Initialize everything
    function init() {
        // Prevent double initialization
        if (state.isInitialized) {

            return;
        }

        // Check if template is active before initializing
        if (!isTemplateActive()) {

            return;
        }

        state.isInitialized = true;

        // Initialize watermark cache for faster processing
        utils.initWatermarkCache();

        // Load active tasks from localStorage
        utils.getActiveTasks();

        // Initialize DOM elements
        initializeElements();

        // Initialize canvas
        canvasManager.init();

        // Initialize cropper
        cropperManager.init();

        // Initialize add to cart button states
        initializeAddToCartStates();

        // Bind event listeners
        if (elements.uploadBtn) {
            elements.uploadBtn.addEventListener("click", modalManager.open);
        }

        if (elements.closeBtn) {
            elements.closeBtn.addEventListener("click", () => {
                if (
                    elements.generatingStep?.style.display === "flex" &&
                    utils.storage.get(CONSTANTS.STORAGE_KEYS.TASK_ID)
                ) {
                    noticeModalManager.show();
                    return;
                }
                modalManager.close();
            });
        }

        if (elements.modal) {
            elements.modal.addEventListener("click", (e) => {
                if (e.target === elements.modal) modalManager.close();
            });
        }

        if (elements.browseLink && elements.fileInput) {
            elements.browseLink.addEventListener("click", (e) => {
                e.preventDefault();
                elements.fileInput.click();
            });
        }

        // if (elements.fileInput) {
        //   elements.fileInput.addEventListener('change', eventHandlers.handleFileInputChange);
        // }

        if (elements.dropZone) {
            elements.dropZone.addEventListener(
                "click",
                eventHandlers.handleDropZoneClick
            );
            elements.dropZone.addEventListener(
                "dragover",
                eventHandlers.handleDragOver
            );
            elements.dropZone.addEventListener(
                "dragleave",
                eventHandlers.handleDragLeave
            );
            elements.dropZone.addEventListener("drop", eventHandlers.handleDrop);
        }

        if (elements.removeBtn) {
            elements.removeBtn.addEventListener("click", uploadManager.removeFile);
        }

        if (elements.generateBtn) {
            elements.generateBtn.addEventListener("click", () =>
                generationManager.generatePreview()
            );
        }

        if (elements.designList) {
            elements.designList.addEventListener(
                "click",
                eventHandlers.handleDesignClick
            );
        }

        // Global event listeners - only if template is active
        if (isTemplateActive()) {
            document.addEventListener("contextmenu", eventHandlers.handleContextMenu);
            window.addEventListener("beforeunload", eventHandlers.handleBeforeUnload);

            // Use event delegation for variant changes and add to cart
            document.addEventListener(
                "change",
                utils.debounce(eventHandlers.handleVariantChange.bind(eventHandlers), 100)
            );
            document.addEventListener(
                "click",
                eventHandlers.handleAddToCart.bind(eventHandlers),
                true
            );
        }

        // Setup variant listener after DOM is ready
        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", () => {
                // Additional initialization if needed
            });
        }
    }

    // Add to cart state management
    function initializeAddToCartStates() {
        function isImageGenerated() {
            return !!utils.storage.get(CONSTANTS.STORAGE_KEYS.TASK_ID);
        }

        function isDesignSelectionVisible() {
            const el = document.getElementById("design-selection");
            return el && window.getComputedStyle(el).display !== "none";
        }

        function setAddToCartEnabled(enabled) {
            const btns = document.querySelectorAll(
                'button[name="add"], input[name="add"], .product-form button[type="submit"], #addToCartBtn'
            );
            btns.forEach(function (btn) {
                btn.disabled = !enabled;
                if (!enabled) {
                    btn.classList.add("disabled");
                } else {
                    btn.classList.remove("disabled");
                }
            });
        }

        // Set initial state - Luôn enable button
        setAddToCartEnabled(true);

        // Handle variant selects clicks - Không chặn nữa
        const addToCartForms = document.querySelectorAll(
            'form[data-type="add-to-cart-form"], form.form'
        );
        addToCartForms.forEach(function (form) {
            const variantSelects = form.querySelector("variant-selects");
            if (variantSelects) {
                variantSelects.addEventListener("click", function (e) {
                    // Không chặn click nữa, để user có thể chọn variant bình thường
                    setAddToCartEnabled(true);
                });
            }
        });

        // Listen for Shopify add to cart button clicks
        document.addEventListener("click", function (e) {
            if (
                e.target.matches(
                    'button[name="add"], input[name="add"], .product-form button[type="submit"]'
                )
            ) {


                // Get product data
                const productData = eventHandlers.getProductData();
                if (productData) {
                    // Get selected variant
                    const form = e.target.closest("form");
                    let selectedVariant = null;

                    if (form) {
                        const variantInput = form.querySelector('input[name="id"]');
                        const selectedVariantId = variantInput
                            ? parseInt(variantInput.value, 10)
                            : null;
                        if (productData?.variants && selectedVariantId) {
                            selectedVariant = productData.variants.find(
                                (v) => v.id === selectedVariantId
                            );
                        }
                    }

                    // If no variant found, use first one
                    if (!selectedVariant && productData.variants) {
                        selectedVariant = productData.variants[0];
                    }

                    // Không cần force tracking nữa vì đã có trong handleAddToCart
                    // if (selectedVariant) {
                    //   setTimeout(() => {
                    //     eventHandlers.handleTracking(selectedVariant, productData);
                    //   }, 100);
                    // }
                }
            }
        });
    }

    // Cleanup function
    function cleanup() {
        // Clear all intervals
        state.intervals.forEach((interval) => clearInterval(interval));
        state.intervals.clear();

        // Close all event sources
        state.eventSources.forEach((sse) => sse.close());
        state.eventSources.clear();

        // Cleanup cropper
        if (state.cropper) {
            state.cropper.destroy();
            state.cropper = null;
        }

        // Remove event listeners
        document.removeEventListener(
            "contextmenu",
            eventHandlers.handleContextMenu
        );
        window.removeEventListener(
            "beforeunload",
            eventHandlers.handleBeforeUnload
        );
    }

    // Initialize when DOM is ready - only once to prevent duplicate tracking
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => {
            if (isTemplateActive()) {
                init();
            } else {

            }
        });
    } else {
        // DOM already loaded, init immediately
        if (isTemplateActive()) {
            init();
        } else {

        }
    }

    // Cleanup on page unload
    window.addEventListener("beforeunload", cleanup);

    // Expose functions globally for external access
    window.customProductFormCleanup = cleanup;
    window.generationManager = generationManager;
    window.uploadManager = uploadManager;
    window.utils = utils; // Expose utils for debugging
    window.eventHandlers = eventHandlers; // Expose eventHandlers for cart cleanup

    // Fallback function for generation
    function triggerGenerationFallback() {


        // Check if we already have generated images
        const taskId = localStorage.getItem("taskId");
        const img1 = localStorage.getItem("generatedImage_" + taskId + "_1");

        if (taskId && img1) {

            if (window.generationManager && window.generationManager.handleGenerationComplete) {
                window.generationManager.handleGenerationComplete({
                    image_runninghub_1: img1
                });
            }
            return;
        }


        utils.showToast("No generated images found. Please upload again.");
    }

    // Expose fallback function
    window.triggerGenerationFallback = triggerGenerationFallback;

    function setupCustomUploadBtnWithProgress() {
        const btn = document.getElementById("custom-upload-btn-wrap");
        if (!btn || !btn.classList.contains("button-custom")) return;
        const uploadButton = btn.querySelector(".custom-upload-button");
        const spanText = btn.querySelector(".custom-upload-button span");
        const loadSVG = btn.querySelector(".load");
        const loadBar = document.getElementById("progress-bar");
        const checkSVG = btn.querySelector(".check");
        const fileInput = document.getElementById("custom-upload-input");

        // Get the product type from the button's data attribute
        const productType = uploadButton.getAttribute('data-product-type');

        // Định nghĩa trực tiếp endpoint trong function này
        const API_BASE_URL = "https://pah.up.railway.app/api";
        const GEMINI_GENERATE_ENDPOINT = "/image-render/generate-with-name";

        let waitingInterval = null;
        let waitingForResponse = false;
        let lastProgress = 0;
        let uploadStartTime = null; // Track when upload process starts for minimum button processing duration

        // Helper: reset previous generation without reloading
        function resetForNewUpload() {
            try {
                // stop local simulated interval if exists
                if (uploadButton._localGenInterval) {
                    clearInterval(uploadButton._localGenInterval);
                    uploadButton._localGenInterval = null;
                }
                // clear global timers and event sources
                state.intervals.forEach((it) => clearInterval(it));
                state.intervals.clear();
                state.eventSources.forEach((sse) => { try { sse.close(); } catch (e) { } });
                state.eventSources.clear();
                if (window.generationManager && window.generationManager.stepProgressionTimer) {
                    try { clearInterval(window.generationManager.stepProgressionTimer); } catch (e) { }
                    window.generationManager.stepProgressionTimer = null;
                }
                // hide generating step and reset progress
                const gen = document.getElementById('main-generating-step');
                if (gen) gen.style.display = 'none';
                if (elements.generatingBar) elements.generatingBar.style.width = '4%';
                if (elements.generatingLabel) elements.generatingLabel.textContent = '4%';
                // hide and clear design selection
                if (elements.designSelection) {
                    elements.designSelection.style.display = 'none';
                    elements.designSelection.innerHTML = '';
                }
                // clear storage keys
                const existingTask = localStorage.getItem('taskId');
                if (existingTask) {
                    try { localStorage.removeItem('generatedImage_' + existingTask + '_1'); } catch (e) { }
                    try { localStorage.removeItem('generatedImage_' + existingTask + '_2'); } catch (e) { }
                    try { localStorage.removeItem(CONSTANTS.STORAGE_KEYS.SELECTED_VERSION + existingTask); } catch (e) { }
                }
                try { localStorage.removeItem('taskId'); } catch (e) { }
                // clean any leftover generatedImage_* keys
                try {
                    const keys = Object.keys(localStorage);
                    keys.forEach((k) => { if (k.indexOf('generatedImage_') === 0) { try { localStorage.removeItem(k); } catch (e) { } } });
                } catch (e) { }
                state.taskId = null;
                state.selectedVersion = null;
                state.isUploadSuccess = false;
                // re-enable upload button visuals
                uploadButton.disabled = false;
                uploadButton.style.pointerEvents = '';
                uploadButton.style.cursor = '';
                uploadButton.removeAttribute('aria-disabled');
                const wrap = document.querySelector('.button-custom');
                if (wrap) { wrap.style.pointerEvents = ''; wrap.style.opacity = ''; wrap.style.cursor = ''; }
            } catch (err) { }
        }

        uploadButton.onclick = function (e) {
            e.preventDefault();
            fileInput.click();
        };

        fileInput.onchange = function () {
            const file = this.files[0];
            if (!file) return;

            // Check if it's an image
            if (!file.type.match('image.*')) {
                utils.showToast('Please select an image file.');
                return;
            }

            // Read file and show cropper modal
            const reader = new FileReader();
            reader.onload = function (event) {
                const imageDataUrl = event.target.result;
                if (typeof imageDataUrl === 'string') {
                    cropperManager.showModal(imageDataUrl);
                }
            };
            reader.onerror = function () {
                utils.showToast('Error reading file.');
            };
            reader.readAsDataURL(file);
            return; // Exit here, let cropper handle the rest
        };

        // Original file processing (kept for compatibility)
        function processFileUpload() {
            if (!fileInput.files || !fileInput.files[0]) return;

            // If previous generation exists or design-selection visible, soft-reset instead of reload
            try {
                const existingTask = localStorage.getItem('taskId');
                const designSel = document.getElementById('design-selection');
                const designVisible = designSel && window.getComputedStyle(designSel).display !== 'none';
                if (existingTask || designVisible) {
                    resetForNewUpload();
                }
            } catch (e) { }

            // Mark the start time of the upload/button process
            uploadStartTime = Date.now();

            // Ẩn Finger Click icon ngay khi chọn hình
            var fingerClickIcon = document.getElementById("finger-click-icon");
            if (fingerClickIcon) {
                fingerClickIcon.style.display = "none";
            }

            // Disable button while API runs; keep text visible and show not-allowed cursor
            uploadButton.disabled = true;
            uploadButton.style.pointerEvents = "none";
            uploadButton.style.cursor = "not-allowed";
            uploadButton.setAttribute("aria-disabled", "true");

            // Immediately show generating step while waiting for API result and start local progress
            (function showGeneratingImmediately() {
                // Hide any existing preview sections
                const previewSection = document.getElementById("upload-preview-section");
                if (previewSection) {
                    previewSection.style.display = "none";
                }

                // Show generating step container
                const generatingStep = document.getElementById("main-generating-step");
                if (generatingStep) {
                    generatingStep.style.display = "block";
                    try { generatingStep.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (err) { }
                    // Reset progress UI if available
                    try {
                        if (window.generationManager && typeof window.generationManager.resetProgress === 'function') {
                            window.generationManager.resetProgress();
                        }
                    } catch (err) { }
                    // Local progress up to 95% while waiting (slower)
                    let p = 4;
                    const updateLocal = () => {
                        const bar = document.querySelector('#main-generating-step .photo-loading-bar-inner') || document.querySelector('.custom-generating-step .photo-loading-bar-inner');
                        const label = document.querySelector('#main-generating-step .photo-loading-bar-label') || document.querySelector('.custom-generating-step .photo-loading-bar-label');
                        if (bar) bar.style.width = p + "%";
                        if (label) label.textContent = p + "%";
                    };
                    updateLocal();
                    const localInterval = setInterval(() => {
                        if (p < 95) {
                            p++;
                            updateLocal();
                        }
                    }, 400);
                    // store interval on element for later cleanup
                    uploadButton._localGenInterval = localInterval;
                } else {
                    const altGeneratingStep = document.querySelector(".custom-generating-step");
                    if (altGeneratingStep) {
                        altGeneratingStep.style.display = "flex";
                    }
                }
            })();

            // Start upload immediately
            uploadFileWithProgress(fileInput.files[0]);
        };

        function startWaitingEffect() {
            let percent = lastProgress;
            if (percent < 60) percent = 60; // đảm bảo không quá thấp
            if (percent > 95) percent = 95;
            if (loadBar) loadBar.style.width = percent + "%";
            waitingForResponse = true;
            waitingInterval = setInterval(() => {
                if (percent < 95) {
                    percent++;
                    if (loadBar) loadBar.style.width = percent + "%";
                }
                // Nếu đã đạt 95% thì giữ nguyên
            }, 1200);
        }

        function stopWaitingEffect() {
            waitingForResponse = false;
            if (waitingInterval) {
                clearInterval(waitingInterval);
                waitingInterval = null;
            }
        }

        function uploadFileWithProgress(file) {
            const xhr = new XMLHttpRequest();
            const formData = new FormData();
            formData.append("file", file);

            // Add loraName to the form data if available
            if (productType) {
                formData.append("loraName", productType);

            } else {

            }

            // Add options field
            formData.append("options", "2");
            // Append childName from modal input if present
            try {
                const nameInput = document.getElementById('kid-message-input');
                if (nameInput && nameInput.value) {
                    formData.append('childName', nameInput.value.trim());
                }
            } catch (_) { }

            // Log the complete FormData contents for debugging

            for (let [key, value] of formData.entries()) {

            }

            xhr.upload.onprogress = function (e) {
                if (e.lengthComputable && !waitingForResponse) {
                    let percent = Math.round((e.loaded / e.total) * 70); // chỉ đến 70%
                    if (percent > 70) percent = 70;
                    lastProgress = percent;
                    if (loadBar) loadBar.style.width = percent + "%";
                }
            };

            xhr.upload.onloadend = function () {
                // Khi upload xong, bắt đầu hiệu ứng chờ response
                if (!waitingForResponse) startWaitingEffect();
            };

            xhr.onload = function () {

                let taskId = null;
                let images = null;
                try {
                    const res = JSON.parse(xhr.responseText);
                    if (res && res.statusCode === 201 && res.data && res.data.taskId && res.data.images) {
                        taskId = res.data.taskId;
                        images = res.data.images;
                        localStorage.setItem("taskId", taskId);

                        // Store product type in localStorage for later use
                        if (productType) {
                            localStorage.setItem("productType", productType);

                        }

                        // Store generated image (chỉ 1 hình)
                        if (images.length >= 1) {
                            localStorage.setItem("generatedImage_" + taskId + "_1", images[0].imageUrl);
                        }

                        // **THÊM DÒNG NÀY: Ẩn design-selection cũ**
                        const oldDesignSelection = document.getElementById("design-selection");
                        if (oldDesignSelection) {
                            oldDesignSelection.style.display = "none";
                        }
                    }
                } catch (e) { }
                if (taskId && images) {
                    stopWaitingEffect();
                    if (loadBar) loadBar.style.width = "100%";

                    // Reset button state immediately
                    setTimeout(function () {
                        if (loadSVG) loadSVG.style.display = "none";
                        if (checkSVG) checkSVG.style.display = "block";
                        setTimeout(function () {
                            var progressContainer = btn.querySelector("div");
                            if (progressContainer) progressContainer.style.display = "none";
                            if (loadBar) loadBar.style.width = "0";
                            if (checkSVG) checkSVG.style.display = "none";
                            btn.style.width = "100%";
                            uploadButton.style.width = "100%";
                        }, 200);
                    }, 300);
                    const proceedToGenerating = function () {
                        // Show generating step after the enforced delay


                        // Hide any existing preview sections
                        const previewSection = document.getElementById("upload-preview-section");
                        if (previewSection) {
                            previewSection.style.display = "none";
                        }

                        // Show generating step
                        const generatingStep = document.getElementById("main-generating-step");
                        if (generatingStep) {
                            generatingStep.style.display = "block";
                            try { generatingStep.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (err) { }
                        } else {
                            // Fallback: tìm kiếm theo class
                            const altGeneratingStep = document.querySelector(".custom-generating-step");
                            if (altGeneratingStep) {
                                altGeneratingStep.style.display = "flex";

                            } else {

                            }
                        }

                        // Start generation process now

                        if (window.generationManager && window.generationManager.generatePreview) {

                            window.generationManager.generatePreview();
                        } else {

                            triggerGenerationFallback();
                        }
                    };

                    // Immediately proceed to generating view
                    proceedToGenerating();
                    // Re-enable button after success
                    uploadButton.disabled = false;
                    uploadButton.style.pointerEvents = "";
                    uploadButton.style.cursor = "";
                    uploadButton.removeAttribute("aria-disabled");
                } else {
                    // Nếu không có taskId, vẫn giữ hiệu ứng chờ
                    if (!waitingForResponse) startWaitingEffect();
                }
            };

            xhr.onerror = function () {
                stopWaitingEffect();
                alert("Upload failed. Please try again.");
                var progressContainer = btn.querySelector("div");
                if (progressContainer) progressContainer.style.display = "none";
                if (loadBar) loadBar.style.width = "0";
                loadSVG.style.display = "none";
                checkSVG.style.display = "none";
                btn.style.width = "100%";
                uploadButton.style.width = "100%";
                if (spanText) spanText.style.opacity = "";
                // Re-enable button on error
                uploadButton.disabled = false;
                uploadButton.style.pointerEvents = "";
                uploadButton.style.cursor = "";
                uploadButton.removeAttribute("aria-disabled");
            };


            xhr.open("POST", API_BASE_URL + GEMINI_GENERATE_ENDPOINT, true);
            xhr.send(formData);
        }

        // Expose processFileUpload globally for cropper to use
        window.processFileUpload = processFileUpload;
    }

    if (document.readyState === "loading") {
        document.addEventListener(
            "DOMContentLoaded",
            setupCustomUploadBtnWithProgress
        );
    } else {
        setupCustomUploadBtnWithProgress();
    }
    // Hàm vẽ mockup Blanket dùng thông số cứng
    function drawMockupBlanketCustom(
        b64,
        canvasId = "blanket-mockup-canvas",
        size = "50x60"
    ) {
        var canvas = document.getElementById(canvasId);
        if (!canvas) return;
        var ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        var userImg = new window.Image();
        userImg.onload = function () {
            var overlay = new window.Image();
            // Chọn overlay theo loại chăn (Fleece/Sherpa) dựa vào SKU hoặc selectedProductType nếu có
            try {
                var typeBtn = document.querySelector('#design-selection .product-type-btn.selected');
                var currentType = typeBtn ? typeBtn.textContent.trim() : 'Fleece Blanket';
                if (currentType === 'Sherpa Blanket') {
                    overlay.src = (size === '50x60')
                        ? 'https://res.cloudinary.com/dmjdplb7d/image/upload/v1761485171/Sherpa_5060_p30pze.png'
                        : 'https://res.cloudinary.com/dmjdplb7d/image/upload/v1761485172/Sherpa_6080_s4xdjj.png';
                } else {
                    overlay.src = (size === '50x60')
                        ? 'https://res.cloudinary.com/dmjdplb7d/image/upload/v1761485171/Fleece_Blanket_5060_hpfq8u.png'
                        : 'https://res.cloudinary.com/dmjdplb7d/image/upload/v1761485172/Fleece_Blanket_6080_m1rtwe.png';
                }
            } catch (_) {
                overlay.src = (size === '50x60')
                    ? 'https://res.cloudinary.com/dmjdplb7d/image/upload/v1761485171/Fleece_Blanket_5060_hpfq8u.png'
                    : 'https://res.cloudinary.com/dmjdplb7d/image/upload/v1761485172/Fleece_Blanket_6080_m1rtwe.png';
            }

            overlay.onload = function () {
                // Scale down: reduce canvas size and adjust drawing area
                var scale = 1; // Scale down to 50%
                var scaledW = canvas.width * scale;
                var scaledH = canvas.height * scale;
                var offsetX = (canvas.width - scaledW) / 2;
                var offsetY = (canvas.height - scaledH) / 2;

                // Blanket specific settings - thông số cụ thể cho blanket (đã thu nhỏ)
                var boxW, boxH, boxX, boxY;
                if (size === "50x60") {
                    boxW = scaledW * 0.65;
                    boxH = scaledH * 0.70;
                    boxX = offsetX + (scaledW - boxW) / 2;
                    boxY = offsetY + (scaledH - boxH) / 2 + scaledH * 0.02;
                } else {
                    boxW = scaledW * 0.60;
                    boxH = scaledH * 0.65;
                    boxX = offsetX + (scaledW - boxW) / 2;
                    boxY = offsetY + (scaledH - boxH) / 2 + scaledH * 0.01;
                }

                var imgRatio = userImg.width / userImg.height;
                var boxRatio = boxW / boxH;
                var drawW, drawH, drawX, drawY;
                if (imgRatio > boxRatio) {
                    drawH = boxH;
                    drawW = boxH * imgRatio;
                } else {
                    drawW = boxW;
                    drawH = boxW / imgRatio;
                }
                if (size === "50x60") {
                    drawX = boxX + (boxW - drawW) / 2 + 100;
                    drawY = boxY + (boxH - drawH) / 2 + 70;
                } else {
                    drawX = boxX + (boxW - drawW) / 2 + 110;
                    drawY = boxY + (boxH - drawH) / 2 + 120;
                }
                ctx.save();
                ctx.drawImage(userImg, drawX, drawY, drawW, drawH);
                ctx.restore();
                ctx.drawImage(overlay, offsetX, offsetY, scaledW, scaledH);
            };
        };
        userImg.src = b64;
    }

    // Thêm lại CSS cho .size-btn.selected nếu chưa có
    (function ensureSizeBtnSelectedCSS() {
        if (!document.getElementById("size-btn-selected-style")) {
            const style = document.createElement("style");
            style.id = "size-btn-selected-style";
            style.innerHTML = `
          .size-btn.selected, .product-type-btn.selected {
            background: #fff !important;
            color: #ff2e8a !important;
            border: 2px solid #ff2e8a !important;
          }
        `;
            document.head.appendChild(style);
        }
    })();
})();

// Chặn quick back khi đang ở custom-generating-step
window.addEventListener("popstate", function (e) {
    var generatingStep = document.querySelector(".custom-generating-step");
    if (generatingStep && generatingStep.style.display === "flex") {
        if (
            window.noticeModalManager &&
            typeof window.noticeModalManager.show === "function"
        ) {
            window.noticeModalManager.show({
                icon: `<svg width="60" height="60" aria-hidden="true"><circle cx="30" cy="30" r="28" fill="#fff" stroke="#4ade80" stroke-width="4"/><text x="30" y="40" text-anchor="middle" font-size="40" fill="#4ade80">!</text></svg>`,
                title: "Notice",
                message: `Please wait for your preview to finish generating.<br><br>Do not exit or go back at this step!`,
                okText: "Ok",
                showExit: false,
            });
        }
        // Push lại state để chặn back tiếp
        window.history.pushState({ generating: true }, "");
    }
});

